/* deploy-marker 2026-10-17T16:05Z — forces a distinct tree + bundle hash to retrigger Pages */
import React, { useState, useEffect, useMemo, useCallback, useRef, useDeferredValue } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { AccountFinal } from '@/api/entities';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Search, Map as MapIcon, List, ChevronDown, BarChart2 } from 'lucide-react';
import { Popover, PopoverStaysOpen, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import ProcurementMap from '@/components/procurement/ProcurementMap';
import LiveRadarList from '@/components/procurement/LiveRadarList';
import ClosedDealDetailPage from '@/components/procurement/ClosedDealDetailPage';
import CompetitorIntelligence from '@/components/procurement/CompetitorIntelligence';
import BenchmarkingScatter from '@/components/procurement/BenchmarkingScatter';
import RecentlyExpiredSummary from '@/components/procurement/RecentlyExpiredSummary';
import CategoryDisplacement from '@/components/procurement/CategoryDisplacement';
import ReplacementCycleView from '@/components/procurement/ReplacementCycleView';
import HardwareReplacementRadar from '@/components/procurement/HardwareReplacementRadar';
import { isHardwareTenant, isHardwareCategory } from '@/lib/replacementCycle';
import RouteToBuy from '@/components/procurement/RouteToBuy';
import { resolveDisplacementScope } from '@/lib/displacementTenantScopes';
import { useTranslation, translate } from '@/components/utils/translations';
import { translateIncumbentSentiment } from '@/lib/accountClassificationLabels';
import { resolveEffectiveTenant } from '@/lib/tenantUtils';
import {
  usesOwnClosedDealsForInsights,
  filterClosedDealsToTenantInsights,
  isDealVisible,
} from '@/lib/procurementVisibility';
import { useProcurementServerTabs } from '@/hooks/useProcurementServerTabs';
import { useUser, useCurrentTenant } from '@/contexts/UserContext';
import { normalizeDeal, getDecisionWindowBandForClosedDeal, canonicalizeSentiment } from '@/utils/procurementDealUtils';
import { resolveDealDisplayValue } from '@/utils/procurementValueUsd';
import {
  getDealCategories,
  getDealOptionCategories,
  getDealCategoryDisplay,
  categoryMatches,
  closedCategoryMatch,
  getDealKey,
  splitDealCategoryString,
  normalizeCategoryValue,
} from '@/utils/procurementCategoryUtils';
import { getCategoriesForTenant, getVendorsForCategories, getDealCategoriesForTenant, dealStringsToInternalCategories, toCanonicalVendorName } from '@/lib/procurementCompetitors';
import { toDisplayCountryName, countryMatchesSelection } from '@/utils/countryCentroids';
import {
  toOfficialCategoryName,
  CATEGORY_KEY_TO_OFFICIAL,
  expandCategoriesForApi,
  encodeCategoriesQueryParam,
} from '@/utils/edtechCategoryNames';
import { buildProcurementCategoryOptions } from '@/lib/procurementCategoryOptions';
import { fetchProductProfiles, fetchFitBulkLatest } from '@/lib/api/fit';
import {
  fetchMyTendersConfig,
  fetchMyTendersAugment,
  logDealView,
  setDealStatus,
  clearDealStatus,
  setDealValueOverride,
  clearDealValueOverride,
  resolveDealId,
  collectDealIds,
  mergeAugmentRecords,
} from '@/lib/api/myTenders';
import { dealMatchesPrimaryProcurementCategories } from '@/lib/procurementTenantLiveDeals';
import MyTendersTab from '@/components/procurement/MyTendersTab';
import { liveDealTypeDisplay, frameworkNamesOf } from '@/lib/procurementMethod';
import { toast } from 'sonner';

// Call-time translation for toasts raised inside callbacks (no hook, so no new deps).
function tr(key, vars) {
  let s = translate(key);
  if (vars) {
    Object.entries(vars).forEach(([k, v]) => {
      s = s.split(`{${k}}`).join(String(v ?? ''));
    });
  }
  return s;
}

function toNumberSafe(value) {
  if (value == null) return 0;
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;

  const s = String(value).trim();
  if (!s) return 0;

  // remove currency symbols, commas, spaces
  const cleaned = s.replace(/[$£€,\s]/g, '');
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : 0;
}

/** Benchmarking: commercial vendor label only; excludes Internal/Custom; tolerant canonical matching */
function toBenchmarkVendorLabel(deal) {
  const raw = (deal?.supplier || '').trim();
  const product = (deal?.product_name || '').trim();
  const combined = `${raw} ${product}`.toLowerCase();

  if (/\binternal\b|\bcustom\b|\bproprietary\b/.test(combined)) {
    return null; // explicitly exclude from benchmarking
  }

  const canon = toCanonicalVendorName(raw, deal?.category);
  if (canon && canon !== 'Unknown' && canon !== 'Internal') return canon;

  // fallback to raw supplier only if it looks like a real commercial vendor label
  if (raw && !/\binternal\b|\bcustom\b/.test(raw.toLowerCase())) {
    return raw;
  }

  return null;
}

function decorateDeal(d) {
  const _statusLower = String(d.status || '').toLowerCase().trim();
  const _sourceStatusLower = String(
    d.source_status ?? d.deal_status ?? d.contract_status ?? ''
  ).toLowerCase().trim();
  const _countryDisplay = toDisplayCountryName(d.country) || '';
  const categoryLabels = getDealCategories(d)
    .map((c) => normalizeCategoryValue(c))
    .filter(Boolean)
    .map((s) => s.toLowerCase());
  const _categoryNorm = new Set(categoryLabels);
  const _contractCategoryNorm = _categoryNorm;
  const _supplierLower = String(d.supplier || '').toLowerCase().trim();
  const _supplierCanonical = (() => {
    const raw = String(d.supplier || '').trim();
    if (!raw) return null;
    const canon = toCanonicalVendorName(raw, d?.category);
    return canon && canon !== 'Unknown' ? canon : null;
  })();
  const _institutionType = d.institution_type || d.account?.institution_type || '';
  const _benchmarkVendor = toBenchmarkVendorLabel(d);
  // Status-specific search fields:
  //   Live   → includes incumbent_vendor (vendor to displace)
  //   Closed → includes framework_name (e.g. G-Cloud)
  // Mirrors BE search behavior and intentionally excludes incumbent_vendor on closed rows.
  const _isLive = _statusLower === 'live';
  const _searchBlob = [
    d.title,
    d.account_name,
    d.category,
    d.product_name,
    d.supplier,
    _isLive ? d.incumbent_vendor : null,
    _isLive ? null : d.framework_name,
  ]
    .map((v) => (v == null ? '' : String(v)))
    .join('\n')
    .toLowerCase();
  return {
    ...d,
    _statusLower,
    _sourceStatusLower,
    _countryDisplay,
    _categoryNorm,
    _contractCategoryNorm,
    _supplierLower,
    _supplierCanonical,
    _institutionType,
    _benchmarkVendor,
    _searchBlob,
    _dedupeKey: getDealKey(d),
  };
}

function stripDecorated(deal) {
  const {
    _statusLower,
    _sourceStatusLower,
    _countryDisplay,
    _categoryNorm,
    _contractCategoryNorm,
    _supplierLower,
    _supplierCanonical,
    _institutionType,
    _benchmarkVendor,
    _searchBlob,
    _dedupeKey,
    ...rest
  } = deal || {};
  return rest;
}

/**
 * Parse date-only string (YYYY-MM-DD) as local midnight.
 * new Date("2026-03-04") parses as UTC midnight, which shifts to previous day in negative UTC offsets.
 * Using local date components avoids that and treats "2026-03-04" as March 4 in user's timezone.
 */
function parseDateOnlyLocal(dateStr) {
  if (!dateStr || dateStr === 'N/A') return null;
  const s = String(dateStr).trim();
  const match = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) {
    const [, y, m, d] = match;
    return new Date(parseInt(y, 10), parseInt(m, 10) - 1, parseInt(d, 10));
  }
  try {
    const d = new Date(s);
    return isNaN(d.getTime()) ? null : d;
  } catch {
    return null;
  }
}

const FIT_NULL_LABELS = new Set(['Insufficient Info', 'Low Evidence']);

function getFitOverrideForSort(deal, fitOverridesByDealId, tenantId) {
  if (!fitOverridesByDealId?.size) return null;
  const id = deal?.fit_deal_id ?? deal?.id;
  const tid = tenantId || deal?.tenant_id || '';
  return fitOverridesByDealId.get(`${tid}:${id}`) ?? fitOverridesByDealId.get(id) ?? null;
}

function valueForLiveDealSort(deal, key, fitOverridesByDealId, tenantId) {
  if (key === 'estimated_value' || key === 'value') {
    const resolved = resolveDealDisplayValue(deal);
    return resolved != null ? resolved : null;
  }
  if (key === 'fit') {
    const override = getFitOverrideForSort(deal, fitOverridesByDealId, tenantId);
    const score = Number(override?.fit_light_score ?? deal?.fit_light_score);
    const label = override?.fit_light_label ?? deal?.fit_light_label;
    if (FIT_NULL_LABELS.has(label)) return null;
    return Number.isFinite(score) ? score : null;
  }
  if (key === 'category') {
    return getDealCategoryDisplay(deal) || deal?.category || '';
  }
  if (key === 'institution') {
    return deal?.institution ?? deal?.account_name ?? '';
  }
  if (key === 'deal_type') {
    // sort on what the Type column actually shows, so the order matches the screen
    const m = liveDealTypeDisplay(deal);
    return m.method || m.framework || '';
  }
  if (key === 'deadline') {
    return deal?.deadline ? new Date(deal.deadline).getTime() : null;
  }
  return deal?.[key];
}

/**
 * Shared sort for Live Radar and Recently Expired (both use `liveSortConfig`).
 */
function sortLiveRadarDeals(rows, sortConfig, fitOverridesByDealId, tenantId) {
  const asc = sortConfig.direction === 'ascending';
  const dir = asc ? 1 : -1;
  return [...rows].sort((a, b) => {
    const key = sortConfig.key;

    if (key === 'deadline') {
      const av = a.deadline ? new Date(a.deadline).getTime() : Infinity;
      const bv = b.deadline ? new Date(b.deadline).getTime() : Infinity;
      return asc ? av - bv : bv - av;
    }

    const av = valueForLiveDealSort(a, key, fitOverridesByDealId, tenantId);
    const bv = valueForLiveDealSort(b, key, fitOverridesByDealId, tenantId);

    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;

    if (typeof av === 'number' && typeof bv === 'number') {
      return (av - bv) * dir;
    }
    return String(av).localeCompare(String(bv), undefined, { sensitivity: 'base' }) * dir;
  });
}

function chunkArray(items, size) {
  const out = [];
  for (let i = 0; i < items.length; i += size) {
    out.push(items.slice(i, i + size));
  }
  return out;
}

const FIT_BULK_LATEST_CHUNK_SIZE = 100;

// [2026-10-21] Was a bare 'http://localhost:4000' on localhost, which matched nothing:
// api/src/server.ts defaults to PORT 3000, so the local branch 503'd even with the API
// running, and /api/procurement/bootstrap returning 503 renders the whole Procurement
// page as zeros — indistinguishable from a tenant that is not wired. Honour
// VITE_API_BASE_URL (the pattern lib/api/exitLeverage.js already uses) and otherwise fall
// back to the deployed API, which is what lib/api/procurementServer.js does on both branches.
const API_BASE_URL = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
  ? (import.meta.env.VITE_API_BASE_URL || 'https://api.neogtm.com')
  : 'https://api.neogtm.com';

// Module-level cache — survives navigation within the same tab
const _moduleCache = {
  deals: null,
  liveRadarDeals: null,
  recentlyClosedLiveDeals: null,
  loadedCategories: null,
  filterOptions: null,
  tenantId: null,
  cachedAt: null,
};
const MODULE_CACHE_TTL = 30 * 60 * 1000; // 30 min

const PROCUREMENT_CACHE_KEY = 'procurement-cache-v7';
const PROCUREMENT_CATEGORY_CACHE_KEY = 'procurement-category-cache-v3';
const PROCUREMENT_LIVE_CACHE_KEY = 'procurement-live-cache-v1';
const PROCUREMENT_RECENTLY_CLOSED_CACHE_KEY = 'procurement-recently-closed-cache-v1';
const LIVE_CACHE_DURATION = 30 * 60 * 1000; // 30 min

/** BE live-table views (table=live). Default omits view= → liveradar. */
const LIVE_VIEW = {
  LIVERADAR: 'liveradar',
  RECENTLY_CLOSED: 'recently_closed',
};

function liveViewQueryParam(view) {
  if (!view || view === LIVE_VIEW.LIVERADAR) return '';
  return `&view=${encodeURIComponent(view)}`;
}

/** OCDS notice dedupe — same tender can republish under multiple notice IDs. */
function dedupeOcdsNotices(deals) {
  const seen = new Set();
  const out = [];
  for (const d of deals || []) {
    const title = (d.title || '').trim();
    const account = (d.account_name || '').trim();
    const country = (d.country || '').trim();
    const valRaw = d.estimated_value ?? d.value ?? '';
    const valStr = typeof valRaw === 'number' ? String(valRaw) : (valRaw || '').trim();
    const deadline = (d.deadline || '').trim();
    const key = `${title}|${account}|${country}|${valStr}|${deadline}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(d);
  }
  return out;
}
const CACHE_DURATION = 30 * 60 * 1000; // 30 min for materially faster repeat loads
const CATEGORY_CACHE_DURATION = 10 * 60 * 1000; // 10 min for category fetches
const MAX_CACHE_DEALS = 35000; // Support 30k+ deals; sessionStorage may hit quota on very large datasets
const MAX_CATEGORY_CACHE_ENTRIES = 5;
const MAX_CATEGORY_CACHE_DEALS_PER_ENTRY = 35000;

function readCategoryCache(tenantId, cacheKey) {
  try {
    const raw = sessionStorage.getItem(`${PROCUREMENT_CATEGORY_CACHE_KEY}-${tenantId}`);
    if (!raw) return null;
    const data = JSON.parse(raw);
    const entry = data[cacheKey];
    if (!entry?.deals?.length) return null;
    if (Date.now() - (entry.cachedAt || 0) > CATEGORY_CACHE_DURATION) return null;
    return entry.deals;
  } catch {
    return null;
  }
}

function writeCategoryCache(tenantId, cacheKey, deals) {
  try {
    const storageKey = `${PROCUREMENT_CATEGORY_CACHE_KEY}-${tenantId}`;
    const raw = sessionStorage.getItem(storageKey);
    const data = raw ? JSON.parse(raw) : {};
    data[cacheKey] = {
      deals: Array.isArray(deals)
        ? deals.slice(0, MAX_CATEGORY_CACHE_DEALS_PER_ENTRY).map(stripDecorated)
        : [],
      cachedAt: Date.now(),
    };
    const keys = Object.keys(data);
    if (keys.length > MAX_CATEGORY_CACHE_ENTRIES) {
      const sorted = keys.sort((a, b) => (data[a].cachedAt || 0) - (data[b].cachedAt || 0));
      for (let i = 0; i < sorted.length - MAX_CATEGORY_CACHE_ENTRIES; i++) {
        delete data[sorted[i]];
      }
    }
    sessionStorage.setItem(storageKey, JSON.stringify(data));
  } catch (e) {
    if (e?.name === 'QuotaExceededError') {
      try {
        const storageKey = `${PROCUREMENT_CATEGORY_CACHE_KEY}-${tenantId}`;
        const raw = sessionStorage.getItem(storageKey);
        const data = raw ? JSON.parse(raw) : {};
        const keys = Object.keys(data);
        if (keys.length > 1) {
          const sorted = keys.sort((a, b) => (data[a].cachedAt || 0) - (data[b].cachedAt || 0));
          delete data[sorted[0]];
          sessionStorage.setItem(storageKey, JSON.stringify(data));
        }
      } catch {}
    }
  }
}

function readProcurementCache(tenantId) {
  try {
    const key = `${PROCUREMENT_CACHE_KEY}-${tenantId}`;
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (data.tenantId && data.tenantId !== tenantId) return null;
    if (Date.now() - (data.cachedAt || 0) > CACHE_DURATION) {
      sessionStorage.removeItem(key);
      return null;
    }
    return data;
  } catch {
    return null;
  }
}

function writeProcurementCache(tenantId, snapshot) {
  const key = `${PROCUREMENT_CACHE_KEY}-${tenantId}`;
  const deals = Array.isArray(snapshot.deals)
    ? snapshot.deals.slice(0, MAX_CACHE_DEALS).map(stripDecorated)
    : undefined;
  for (let cap = deals?.length ?? 0; cap > 0; cap = Math.floor(cap * 0.7)) {
    try {
      const bootstrap = {
        tenantId,
        cachedAt: Date.now(),
        loadedCategories: snapshot.loadedCategories,
        filters: snapshot.filters ? snapshot.filters : undefined,
        deals: deals?.slice(0, cap) ?? undefined,
      };
      sessionStorage.setItem(key, JSON.stringify(bootstrap));
      return;
    } catch (e) {
      if (e?.name !== 'QuotaExceededError' || cap <= 1000) return;
    }
  }
}

function readLiveCache(tenantId) {
  try {
    const raw = sessionStorage.getItem(`${PROCUREMENT_LIVE_CACHE_KEY}-${tenantId}`);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (data.tenantId !== tenantId) return null;
    if (Date.now() - (data.cachedAt || 0) > LIVE_CACHE_DURATION) {
      sessionStorage.removeItem(`${PROCUREMENT_LIVE_CACHE_KEY}-${tenantId}`);
      return null;
    }
    return data.deals ?? null;
  } catch {
    return null;
  }
}

function writeLiveCache(tenantId, liveDeals) {
  try {
    sessionStorage.setItem(
      `${PROCUREMENT_LIVE_CACHE_KEY}-${tenantId}`,
      JSON.stringify({ tenantId, cachedAt: Date.now(), deals: (liveDeals || []).map(stripDecorated) })
    );
  } catch (e) {
    if (e?.name === 'QuotaExceededError') {
      try {
        sessionStorage.removeItem(`${PROCUREMENT_CACHE_KEY}-${tenantId}`);
        sessionStorage.setItem(
          `${PROCUREMENT_LIVE_CACHE_KEY}-${tenantId}`,
          JSON.stringify({ tenantId, cachedAt: Date.now(), deals: liveDeals.slice(0, 1000).map(stripDecorated) })
        );
      } catch {}
    }
  }
}

function readRecentlyClosedCache(tenantId) {
  try {
    const raw = sessionStorage.getItem(`${PROCUREMENT_RECENTLY_CLOSED_CACHE_KEY}-${tenantId}`);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (data.tenantId !== tenantId) return null;
    if (Date.now() - (data.cachedAt || 0) > LIVE_CACHE_DURATION) {
      sessionStorage.removeItem(`${PROCUREMENT_RECENTLY_CLOSED_CACHE_KEY}-${tenantId}`);
      return null;
    }
    return data.deals ?? null;
  } catch {
    return null;
  }
}

function writeRecentlyClosedCache(tenantId, deals) {
  try {
    sessionStorage.setItem(
      `${PROCUREMENT_RECENTLY_CLOSED_CACHE_KEY}-${tenantId}`,
      JSON.stringify({ tenantId, cachedAt: Date.now(), deals: (deals || []).map(stripDecorated) })
    );
  } catch (e) {
    if (e?.name === 'QuotaExceededError') {
      try {
        sessionStorage.setItem(
          `${PROCUREMENT_RECENTLY_CLOSED_CACHE_KEY}-${tenantId}`,
          JSON.stringify({ tenantId, cachedAt: Date.now(), deals: (deals || []).slice(0, 1000).map(stripDecorated) })
        );
      } catch {}
    }
  }
}

function tenantMatches(record, tenantId) {
  if (!record) return false;
  if (record.tenant_id === tenantId) return true;
  if (record.tenant_id === 'global') return true;
  if (record.tenant_id == null || record.tenant_id === '') return true;
  if (Array.isArray(record.visible_to_tenants) && record.visible_to_tenants.includes(tenantId)) return true;
  return false;
}

let _cachedAccountList = null;
let _cachedAccountListTenant = null;

async function getCachedAccountList(tenantId) {
  if (_cachedAccountList && _cachedAccountListTenant === tenantId) {
    return _cachedAccountList;
  }
  const raw = await AccountFinal.list();
  const toArray = (r) => Array.isArray(r) ? r : r?.data ?? r?.records ?? r?.items ?? [];
  _cachedAccountList = toArray(raw).filter((r) => tenantMatches(r, tenantId));
  _cachedAccountListTenant = tenantId;
  return _cachedAccountList;
}

function mergeStatusSlice(prev, incoming, status, { replace = false } = {}) {
  const targetStatus = status.toLowerCase();
  const other = prev.filter((d) => (d.status || '').toLowerCase() !== targetStatus);
  const existing = replace ? [] : prev.filter((d) => (d.status || '').toLowerCase() === targetStatus);
  const merged = new Map();
  [...existing, ...incoming].forEach((d) => {
    merged.set(getDealKey(d), d);
  });
  return [...other, ...Array.from(merged.values())];
}

export default function ProcurementPage({ currentTenant }) {
  const { t, language } = useTranslation();
  const [deals, setDeals] = useState([]);
  /** Live Radar slice — BE view=liveradar (default). Separate from recently-closed live rows. */
  const [liveRadarDeals, setLiveRadarDeals] = useState([]);
  /** Recently Closed slice — BE view=recently_closed on table=live. */
  const [recentlyClosedLiveDeals, setRecentlyClosedLiveDeals] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hydratedFromCache, setHydratedFromCache] = useState(false);
  const [effectiveTenantId, setEffectiveTenantId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  // Debounced mirror of searchTerm — used by heavy filter/sort memos (60k+ rows).
  // Input still binds to searchTerm for instant typing feedback.
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearchTerm(searchTerm), 200);
    return () => clearTimeout(id);
  }, [searchTerm]);
  const [activeTab, setActiveTab] = useState('live');
  /* [2026-11-01] The review-reminder email's one call to action is ?tab=my-tenders — it opens My
   * Deals, where the rep sets a status. Read once; the switch waits for the My Deals config, because
   * the tab does not exist until it is known to be enabled. */
  const [searchParams, setSearchParams] = useSearchParams();
  const wantedTabRef = useRef(searchParams.get('tab'));
  const navigate = useNavigate();
  const location = useLocation();
  const [mapVisible, setMapVisible] = useState(true);
  const [mapMetric, setMapMetric] = useState('deals');
  const [mapZoomCountry, setMapZoomCountry] = useState(null);
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [selectedClosedDeal, setSelectedClosedDeal] = useState(null);
  /* [2026-11-01] A closed deal has its own address: /Procurement?deal=<deal_id>. Opening one pushes a
   * history entry, so the browser's back button and the page's Back both return to where the user
   * was — Contract Intelligence with its filters, or the account page that linked here. The URL is
   * the source of truth; selectedClosedDeal only carries the list row so the page paints at once. */
  const dealParam = searchParams.get('deal');
  const openClosedDeal = useCallback((deal) => {
    const id = resolveDealId(deal);
    if (!id) return;
    setSelectedClosedDeal(deal);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('deal', id);
      return next;
    });
  }, [setSearchParams]);
  useEffect(() => {
    if (!dealParam) {
      setSelectedClosedDeal((prev) => (prev ? null : prev));
      return;
    }
    setSelectedClosedDeal((prev) => (prev && resolveDealId(prev) === dealParam ? prev : { deal_id: dealParam }));
  }, [dealParam]);
  const closeClosedDeal = useCallback(() => {
    // location.key is 'default' only when this page was the first one loaded (a shared link):
    // there is nothing to go back to, so drop the param and land on Contract Intelligence.
    if (location.key && location.key !== 'default') {
      navigate(-1);
      return;
    }
    setActiveTab('contract');
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('deal');
      return next;
    }, { replace: true });
  }, [location.key, navigate, setSearchParams]);
  // [2026-11-02] Tiago: land on Live deals sorted by Fit, best fit first and out-of-scope rows last
  // (Fit header, descending). Everything else on the page is unchanged.
  const [liveSortConfig, setLiveSortConfig] = useState({ key: 'fit', direction: 'descending' });
  const [closedSortConfig, setClosedSortConfig] = useState({ key: 'account_name', direction: 'ascending' });
  const [liveSelectedCategories, setLiveSelectedCategories] = useState([]);
  const [recentlySelectedCategories, setRecentlySelectedCategories] = useState([]);
  const [closedSelectedCategories, setClosedSelectedCategories] = useState([]);
  const [institutionTypes, setInstitutionTypes] = useState([]);
  const filtersInitializedRef = useRef(false);
  const liveCategoryFilterTouchedRef = useRef(false);
  /**
   * Recently Expired seeds its category filter with the tenant's own categories the first time the
   * tab is opened — the same default Contract Intelligence applies (see the filtersInitializedRef
   * effect). Seeded on TAB ACTIVATION rather than at filter-init because categoryOptions is built
   * from the loaded deals and is still filling while the page boots; by the time the tab is clicked
   * it is complete, so the tenant categories actually resolve. Set once, and set as soon as the user
   * edits the filter, so a deliberate Clear All / Show All is never overwritten on a later visit.
   */
  const recentlyCategoryDefaultAppliedRef = useRef(false);
  const [deadlineHorizon, setDeadlineHorizon] = useState('all');
  const [decisionWindows, setDecisionWindows] = useState([]);
  const [contractInstitutionTypes, setContractInstitutionTypes] = useState([]);
  const [contractSentiments, setContractSentiments] = useState([]);
  // [2026-10-10] Status filter. The API used to hard-code status='Active' on closed
  // deals, which hid 73% of hardware/equipment rows - README §0.8 makes `Expired` the
  // canonical status for a one-off purchase, so Active-only hid the category's normal
  // state. The server now returns every status; this is how a user narrows it back.
  const [contractStatuses, setContractStatuses] = useState([]);
  const [contractVendors, setContractVendors] = useState([]);
  const [contractProductNames, setContractProductNames] = useState([]);
  /* [2026-10-13] Reseller filter. `reseller` has been in CLOSED_DEAL_LIST_COLUMNS since
   * 2026-10-05 for the Contract Intelligence Reseller column, so the field is already on
   * every row the client holds - this filters on what is there rather than refetching.
   * Sparse on purpose: 10,397 of 233,453 visible closed deals carry one, across 1,849
   * distinct resellers, so the option list is built from the loaded rows like Product Name. */
  const [contractResellers, setContractResellers] = useState([]);
  /* [2026-10-13] Framework filter. Options are the normalised route names the API tags each closed
   * row with (framework_routes, from route_families) plus the raw name where no family matched;
   * procedures (Trato Directo, Procédure ouverte) are never offered. On the server path it narrows
   * the contract TABLE only (p_frameworks) — the map/KPI aggregate is deliberately not filtered. */
  const [contractFrameworks, setContractFrameworks] = useState([]);
  const [contractExitFilter, setContractExitFilter] = useState('all');
  const [benchmarkSelectedVendor, setBenchmarkSelectedVendor] = useState(null);
  /** Live fit overrides from LiveRadarList — used when sorting by fit column. */
  const [liveFitOverrides, setLiveFitOverrides] = useState(() => new Map());
  const [categoryFilterQuery, setCategoryFilterQuery] = useState('');
  const [categoryLoading, setCategoryLoading] = useState(false);
  const [vendorFilterQuery, setVendorFilterQuery] = useState('');
  const [productNameFilterQuery, setProductNameFilterQuery] = useState('');
  const [resellerFilterQuery, setResellerFilterQuery] = useState('');
  const [frameworkFilterQuery, setFrameworkFilterQuery] = useState('');
  const [countryFilterQuery, setCountryFilterQuery] = useState('');

  /**
   * [2026-10-17] ONE filter popover open at a time.
   *
   * These are Radix popovers with modal={false} and, until now, no coordination, so every one you
   * opened stayed open: screenshots of the live page repeatedly caught Country and Category open
   * together, overlapping, with one list showing through the other. Two costs, and the second is
   * the reported bug:
   *   1. Overlapping panels - you cannot tell which list you are looking at.
   *   2. Each open popover is its own DismissableLayer. A real pointerdown on a button in a lower
   *      layer is "outside" for the upper one, so that press is spent dismissing the upper layer and
   *      the re-render moves the button before pointerup - no click event ever fires. The FIRST
   *      click is eaten; later ones work because only one layer is left. That is exactly
   *      "Select all and Clear all do nothing, but picking individual countries works": the
   *      buttons sit at the top of the panel, in the overlap, and take the first press.
   * Synthetic .click() never reproduced it because it skips the pointer sequence entirely.
   */
  const [openFilter, setOpenFilter] = useState(null);
  const aliveRef = useRef(true);
  const tenantIdRef = useRef(null);
  const loadVersionRef = useRef(0);
  const categoryFetchCacheRef = useRef(new Map());
  const categoryFetchInProgressRef = useRef(new Set());
  const categoryFetchDoneRef = useRef(true);
  const closedFetchDebounceRef = useRef(null);
  const fetchDataCalledRef = useRef(false);
  const pendingToastsRef = useRef([]);
  const currentDealsCountRef = useRef(0);
  const priorDealsForMergeRef = useRef(null);
  const hydratedFromCacheRef = useRef(false);
  const dealsRef = useRef([]);
  const lastTenantResetRef = useRef(null);
  const [loadedCategories, setLoadedCategories] = useState(new Set());
  const [filterOptions, setFilterOptions] = useState({ categories: [], countries: [], institutionTypes: [], suppliers: [] });
  const [activeProductProfiles, setActiveProductProfiles] = useState([]);
  const [myTendersConfig, setMyTendersConfig] = useState({ enabled: false });
  useEffect(() => {
    if (wantedTabRef.current === 'my-tenders' && myTendersConfig?.enabled) {
      wantedTabRef.current = null;
      setActiveTab('my-tenders');
    }
  }, [myTendersConfig?.enabled]);
  const [dealAugmentByDealId, setDealAugmentByDealId] = useState({});
  const [dealAugmentError, setDealAugmentError] = useState(false);
  const pendingStatusDealIdsRef = useRef(new Set());
  const recentlyDealSnapshotRef = useRef(new Map());
  const myTendersTabRef = useRef(null);
  const [stableTenantId, setStableTenantId] = useState(null);
  const tenantSessionRef = useRef(0);

  const contextUser = useUser();
  const contextTenant = useCurrentTenant();
  const effectiveCurrentTenant = currentTenant || contextTenant;

  useEffect(() => {
    let cancelled = false;
    if (!contextUser) return;
    const resolved = resolveEffectiveTenant(
      contextUser?.role === 'admin' ? localStorage.getItem('admin-active-tenant') : null,
      effectiveCurrentTenant,
      contextUser,
      'instructure'
    );
    if (cancelled || !resolved) return;
    setStableTenantId((prev) => {
      if (prev === resolved) return prev;
      if (
        prev &&
        prev !== 'instructure' &&
        resolved === 'instructure' &&
        contextUser?.tenant_id &&
        contextUser.tenant_id !== 'instructure'
      ) {
        return prev;
      }
      return resolved;
    });
    return () => {
      cancelled = true;
    };
  }, [effectiveCurrentTenant, contextUser]);

  const countClosed = (arr) =>
    (arr || []).filter((d) => (d.status || '').toLowerCase() !== 'live').length;

  const applyDealsUpdate = useCallback((updater, meta = {}) => {
    const base = dealsRef.current || [];
    const next = updater(base);
    const seen = new Set();
    const deduped = [];
    for (const d of next || []) {
      const key = d?._dedupeKey || getDealKey(d);
      if (!key) {
        deduped.push(d);
        continue;
      }
      if (seen.has(key)) continue;
      seen.add(key);
      deduped.push(d);
    }

    const baseClosed = countClosed(base);
    const nextClosed = countClosed(deduped);

    if (!meta.allowShrink && nextClosed < baseClosed) {
      console.warn('[Procurement] ⛔ Blocked closed-deals shrink', {
        source: meta.source || 'unknown',
        baseClosed,
        nextClosed,
      });
      return base;
    }

    dealsRef.current = deduped;
    setDeals(deduped);
    return deduped;
  }, []);

  const hydrateLiveDealsWithFit = useCallback(async (tenantId, liveDeals) => {
    if (!tenantId || !Array.isArray(liveDeals) || liveDeals.length === 0) {
      return liveDeals || [];
    }

    const fitDealIds = [...new Set(
      liveDeals.map((deal) => deal?.fit_deal_id).filter(Boolean)
    )];

    if (fitDealIds.length === 0) {
      return liveDeals;
    }

    try {
      const chunks = chunkArray(fitDealIds, FIT_BULK_LATEST_CHUNK_SIZE);
      const merged = {};

      for (const chunk of chunks) {
        const partial = await fetchFitBulkLatest(tenantId, chunk);
        Object.assign(merged, partial || {});
      }

      const fitMap = merged;

      return liveDeals.map((deal) => {
        const fit = fitMap?.[deal.fit_deal_id] || {};

        return {
          ...deal,
          fit_light_label: fit.light?.label ?? deal.fit_light_label ?? null,
          fit_light_score: fit.light?.score ?? deal.fit_light_score ?? null,
          fit_light_outcome: fit.light?.outcome ?? deal.fit_light_outcome ?? null,
          fit_job_status: fit.light_job?.status ?? deal.fit_job_status ?? null,
        };
      });
    } catch (err) {
      console.error('[Procurement] bulk fit hydrate failed:', err?.message || err, err);
      return liveDeals;
    }
  }, []);

  const QUERY_PAGE_SIZE = 1000;
  // [2026-10-10] 250 -> 1000. This was sized when the server filtered closed deals
  // to status='Active', so a filtered category was small. With that filter gone a
  // single category returns 37,577 rows, and at 250 the client issued ~151 requests
  // PER SLICE (dated + no_date run in parallel) — which is what made Contract
  // Intelligence time out. The server clamp was raised to 1000 in the same change;
  // measured server-side per page on the biggest category: 300->65ms, 1000->61ms,
  // so a 1000-row page costs no more than a 250-row one and needs a quarter as many.
  const QUERY_PAGE_SIZE_FILTERED = 1000;
  // Background closed-deal loader paginates without a category filter, so it can hit
  // the cap on tenants with large global datasets (60k+). 300 pages × 1000 rows =
  // ~300k row headroom per parallel slice (dated + no_date), ~600k total — enough
  // for current scale. Revisit if we cross 100k deals or if "hit page limit" warnings reappear.
  const MAX_PAGES = 300;

  async function loadRemaining(table, cursor, tenantId, categories = null, limit = QUERY_PAGE_SIZE, extraParam = '') {
    let allItems = [];
    let currentCursor = cursor;
    const categoriesParam = categories?.length
      ? `&categories=${encodeCategoriesQueryParam(categories)}`
      : '';
    // [2026-10-10] This loop was the ONLY pager without a page cap, and it stopped
    // mattering the moment the server stopped filtering to status='Active': a single
    // category can now return 37,000 rows instead of 12,000. Capped like its siblings.
    let _page = 0;
    while (currentCursor) {
      if (++_page > MAX_PAGES) { console.warn(`⚠️ loadRemaining hit page limit (${MAX_PAGES})`); break; }
      const res = await fetch(
        `${API_BASE_URL}/api/procurement/query?table=${table}&limit=${limit}${categoriesParam}&skipFit=true${extraParam}&cursor=${encodeURIComponent(JSON.stringify(currentCursor))}`,
        { headers: { 'x-tenant-id': tenantId }, credentials: 'include' }
      );
      const page = await res.json().catch(() => ({}));
      if (!res.ok) {
        const msg = page?.error || page?.message || `HTTP ${res.status}`;
        console.warn('[Procurement] ⚠️ loadRemaining HTTP error:', msg);
        if (res.status === 500 || String(msg).includes('timeout') || String(msg).includes('canceling')) {
          toast.warning(tr('procToastDataIncomplete'), {
            description: tr('procToastTimeoutPage'),
            duration: 6000,
          });
        }
        break;
      }
      if (page.error) {
        console.warn('[Procurement] ⚠️ loadRemaining page error:', page.error);
        if (String(page.error).includes('timeout') || String(page.error).includes('canceling')) {
          toast.warning(tr('procToastDataIncomplete'), {
            description: tr('procToastTimeoutRecords'),
            duration: 6000,
          });
          break;
        }
      }
      allItems = allItems.concat(page.data || []);
      currentCursor = page.nextCursor;
    }
    return allItems;
  }

  async function fetchAllForCategories(table, categoriesParam, tenantId, limit = QUERY_PAGE_SIZE_FILTERED, onProgress = null) {
    const baseUrl = `${API_BASE_URL}/api/procurement/query?table=${table}&categories=${categoriesParam}&limit=${limit}&skipFit=true`;

    // Live table: simple single-cursor path is fine
    if (table !== 'closed') {
      const result = await fetchPaginated(baseUrl, '', tenantId, onProgress);
      return { items: result.items, complete: result.complete };
    }

    // Closed table MUST split dated + null expiry rows in parallel.
    // The normal closed cursor advances on predicted_expiry, so IS NULL rows
    // fall out of pagination unless fetched separately — same as fetchAllNoCategories().
    const progress = { dated: 0, noDate: 0 };
    const relay = (bucket) => (_page, total) => {
      progress[bucket] = total;
      onProgress?.(_page, progress.dated + progress.noDate);
    };

    const [dated, noDate] = await Promise.all([
      fetchPaginated(baseUrl, '&expiryFilter=dated', tenantId, relay('dated')).catch((e) => {
        console.warn('⚠️ category dated phase failed:', e?.message);
        return { items: [], complete: false };
      }),
      fetchPaginated(baseUrl, '&expiryFilter=no_date', tenantId, relay('noDate')).catch((e) => {
        console.warn('⚠️ category no_date phase failed:', e?.message);
        return { items: [], complete: false };
      }),
    ]);

    return {
      items: [...(dated.items || []), ...(noDate.items || [])],
      complete: (dated.complete ?? true) && (noDate.complete ?? true),
    };
  }

  async function fetchPaginated(baseUrl, extraParam, tenantId, onProgress) {
    let allItems = [];
    let currentCursor = null;
    let page = 0;
    let complete = true;
    do {
      page++;
      if (page > MAX_PAGES) {
        complete = false;
        console.warn(`⚠️ hit max pages (${MAX_PAGES}), stopping`);
        break;
      }
      const url = currentCursor
        ? `${baseUrl}${extraParam}&cursor=${encodeURIComponent(JSON.stringify(currentCursor))}`
        : `${baseUrl}${extraParam}`;
      const res = await fetch(url, { headers: { 'x-tenant-id': tenantId }, credentials: 'include' });
      const pageData = await res.json();
      if (!res.ok) {
        complete = false;
        if (page === 1) throw new Error(pageData?.error || `Request failed: ${res.status}`);
        console.warn(`⚠️ page ${page} failed (${res.status}), returning partial data`);
        break;
      }
      const items = pageData.data || [];
      allItems = allItems.concat(items);
      currentCursor = pageData.nextCursor;
      if (onProgress && page > 1) onProgress(page, allItems.length);
    } while (currentCursor);
    return { items: allItems, complete };
  }

  /** Paginate table=live with optional BE view (liveradar default | recently_closed). No categories unless passed. */
  async function fetchAllLiveView(
    tenantId,
    { view = LIVE_VIEW.LIVERADAR, categories = null, limit = QUERY_PAGE_SIZE, onProgress = null } = {}
  ) {
    const categoriesParam = categories?.length
      ? `&categories=${encodeCategoriesQueryParam(categories)}`
      : '';
    const viewParam = liveViewQueryParam(view);
    let allItems = [];
    let currentCursor = null;
    let page = 0;
    do {
      page++;
      if (page > MAX_PAGES) {
        console.warn(`⚠️ live/${view} hit page limit (${MAX_PAGES})`);
        break;
      }
      const url = currentCursor
        ? `${API_BASE_URL}/api/procurement/query?table=live&limit=${limit}&skipFit=true${viewParam}${categoriesParam}&cursor=${encodeURIComponent(JSON.stringify(currentCursor))}`
        : `${API_BASE_URL}/api/procurement/query?table=live&limit=${limit}&skipFit=true${viewParam}${categoriesParam}`;
      const res = await fetch(url, { headers: { 'x-tenant-id': tenantId }, credentials: 'include' });
      const pageData = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (page === 1) throw new Error(pageData?.error || `Request failed: ${res.status}`);
        console.warn(`⚠️ live/${view} page ${page} failed (${res.status}), returning partial data`);
        break;
      }
      const items = pageData.data || [];
      allItems = allItems.concat(items);
      currentCursor = pageData.nextCursor;
      if (onProgress && page > 1) onProgress(page, allItems.length);
    } while (currentCursor);
    return allItems;
  }

  async function fetchAllNoCategories(table, tenantId, limit = QUERY_PAGE_SIZE, onProgress = null) {
    async function fetchSimple(extraParam = '', progressCb = null) {
      let allItems = [];
      let currentCursor = null;
      let page = 0;
      do {
        page++;
        if (page > MAX_PAGES) { console.warn(`⚠️ ${table} hit page limit (${MAX_PAGES})`); break; }
        const url = currentCursor
          ? `${API_BASE_URL}/api/procurement/query?table=${table}&limit=${limit}&skipFit=true${extraParam}&cursor=${encodeURIComponent(JSON.stringify(currentCursor))}`
          : `${API_BASE_URL}/api/procurement/query?table=${table}&limit=${limit}&skipFit=true${extraParam}`;
        const res = await fetch(url, { headers: { 'x-tenant-id': tenantId }, credentials: 'include' });
        const pageData = await res.json().catch(() => ({}));
        const items = pageData.data || [];
        allItems = allItems.concat(items);
        currentCursor = pageData.nextCursor;
        if (progressCb && page > 1) progressCb(page, allItems.length);
      } while (currentCursor);
      return allItems;
    }

    // Live table uses fetchAllLiveView — not this helper.
    if (table === 'live') {
      return fetchAllLiveView(tenantId, { limit, onProgress });
    }

    // Closed table: fetch dated + null rows in parallel to avoid cursor bridging
    const [datedItems, nullItems] = await Promise.all([
      fetchSimple('&expiryFilter=dated').catch((e) => {
        console.warn('⚠️ dated phase failed:', e?.message);
        return [];
      }),
      fetchSimple('&expiryFilter=no_date').catch((e) => {
        console.warn('⚠️ null phase failed:', e?.message);
        return [];
      }),
    ]);
    return [...datedItems, ...nullItems];
  }

  const fetchData = useCallback(async () => {
    if (!hydratedFromCacheRef.current) setIsLoading(true);
    let bootstrapToastId;
    try {
      bootstrapToastId = toast.loading(tr('procToastLoadingDeals'), {
        description: tr('procToastFetchingInitialData'),
        duration: Infinity,
      });
      const tenantId = tenantIdRef.current;
      if (!tenantId) return;

      const runId = ++loadVersionRef.current;

      // Bootstrap: closed_deals sample + filter metadata only (live slices use /query?table=live).
      const res = await fetch(`${API_BASE_URL}/api/procurement/bootstrap?liveLimit=1&closedLimit=2500`, {
        headers: {
          'x-tenant-id': tenantId,
        },
        credentials: 'include',
      });
      const bootstrap = await res.json();

      const closedPayload = bootstrap.closed ?? bootstrap.closed_deals;

      const accData = await getCachedAccountList(tenantId);

      const accountMap = accData.reduce((m, a) => {
        const tid = a.tenant_id || 'unknown';
        if (a.account_id) m[`${tid}:${a.account_id}`] = a;
        if (a.account_name) m[`${tid}:${(a.account_name || '').trim()}`] = a;
        return m;
      }, {});

      const enrich = (arr, status) =>
        arr
          .map((raw) => {
            const d = normalizeDeal(raw, raw?.account) || raw;
            const tid = d?.tenant_id || raw?.tenant_id || 'unknown';

            const candidate =
              (raw?.account_id && accountMap[`${tenantId}:${raw.account_id}`]) ||
              (raw?.account_name && accountMap[`${tenantId}:${(raw.account_name || '').trim()}`]) ||
              (raw?.account_id && accountMap[`${tid}:${raw.account_id}`]) ||
              (raw?.account_name && accountMap[`${tid}:${(raw.account_name || '').trim()}`]) ||
              null;

            const dealIsGlobalLike =
              tid === 'global' || tid == null || tid === '' || tid === 'unknown';

            if (candidate && !dealIsGlobalLike && candidate.tenant_id !== tid) {
              return decorateDeal({
                ...d,
                source_status: d.status ?? raw?.status ?? null,
                account: null,
                status,
              });
            }

            return decorateDeal({
              ...d,
              source_status: d.status ?? raw?.status ?? null,
              account: candidate ?? null,
              status,
            });
          })
          .filter((d) => d && (d.id || d.deal_id || d.tender_id));

      const closedItems = closedPayload?.items || [];
      const enrichedClosed = enrich(closedItems, 'Closed');

      if (!aliveRef.current) {
        toast.dismiss(bootstrapToastId);
        return;
      }

      const prior = priorDealsForMergeRef.current ?? (dealsRef.current?.length > 0 ? dealsRef.current : null);
      priorDealsForMergeRef.current = null;
      const closedToSet = prior?.length
        ? mergeStatusSlice(prior, enrichedClosed, 'Closed')
        : enrichedClosed;

      applyDealsUpdate(() => closedToSet, { source: 'bootstrap-closed' });
      setFilterOptions(bootstrap.filters || { categories: [], countries: [], institutionTypes: [], suppliers: [] });

      const liveToastId = toast.loading(tr('procToastLoadingMoreDeals'), {
        description: tr('procToastFetchingLiveRadarDeals'),
        duration: Infinity,
      });
      const onLiveProgress = (_page, total) => {
        toast.loading(tr('procToastLoadingMoreDeals'), {
          id: liveToastId,
          description: tr('procToastFetchedLiveDeals', { n: total.toLocaleString() }),
          duration: Infinity,
        });
      };

      try {
        const liveData = await fetchAllLiveView(tenantId, {
          view: LIVE_VIEW.LIVERADAR,
          limit: QUERY_PAGE_SIZE,
          onProgress: onLiveProgress,
        });
        if (runId !== loadVersionRef.current || !aliveRef.current) {
          toast.dismiss(liveToastId);
          return;
        }
        const enrichedLive = enrich(liveData, 'Live');
        const enrichedLiveWithFit = await hydrateLiveDealsWithFit(tenantId, enrichedLive);
        setLiveRadarDeals(enrichedLiveWithFit);
        writeLiveCache(tenantId, enrichedLiveWithFit);
        toast.dismiss(liveToastId);
        pendingToastsRef.current.push({
          label: tr('liveRadar'),
          status: 'live',
          activeTab: 'live',
          fetchedCount: enrichedLiveWithFit.length,
        });
      } catch (e) {
        toast.dismiss(liveToastId);
        toast.error(tr('procToastFailedLiveDeals'), { description: e?.message });
        console.warn('Live Radar load failed:', e?.message);
      }

      setIsLoading(false);
      hydratedFromCacheRef.current = true;
      setHydratedFromCache(true);
      toast.dismiss(bootstrapToastId);

      // Recently Closed — inverse live slice from same deals table (background).
      //
      // [2026-10-04] TWO-PHASE LOAD. Measured before changing anything: this fetched the WHOLE
      // slice unfiltered — 7 sequential cursor-paginated requests, 6,291 rows, to display the 147
      // that survive efekta's category filter, plus fit hydration and my-tenders augment chunked at
      // 100 over all 6,291. Since the tab now defaults to the tenant's own categories, phase 1 asks
      // the BE for just those (1 request, ~147 rows) so the tab is usable almost immediately.
      //
      // Phase 2 then fetches the full slice and replaces it, so "Show all" and any category the
      // user ticks still have their rows. Filtering the fetch WITHOUT phase 2 would make "Show all"
      // silently render 147 and look like the whole market — the under-report class this estate has
      // been bitten by before. Nothing about the final state changes; only the order it arrives in.
      //
      // [2026-10-17] THAT JUSTIFICATION NO LONGER HOLDS, AND THIS IS WHY IT IS STILL SAFE.
      // It used to read: safe to filter server-side because the BE applies `categories` as
      // .in('category', cats) while `matched_categories` is deliberately NOT returned, so the
      // client's getDealCategories() union sees `category` alone — identical to this predicate.
      // `matched_categories` IS now projected (LIVE_DEAL_LIST_COLUMNS), so the client union is
      // strictly WIDER than this predicate: a bundled deal whose primary category sits outside the
      // lens is missing from the phase-1 paint. Phase 2 below fetches the slice with NO category
      // param and replaces the state wholesale, so the settled view carries those rows and the
      // client filter decides on the union. The two phases therefore differ for ~1-2s on bundled
      // rows only, which is the same under-report the phase-2 note above exists to prevent —
      // bounded, not permanent. Do not add `categories` to the phase-2 call to "match" phase 1;
      // that is what would make it permanent.
      (async () => {
        const current = () => runId === loadVersionRef.current && aliveRef.current;
        const prepare = async (rows) => hydrateLiveDealsWithFit(tenantId, enrich(rows, 'Live'));
        try {
          const tenantCats = getDealCategoriesForTenant(tenantId) || [];
          let fastPainted = false;

          if (tenantCats.length) {
            try {
              const fast = await fetchAllLiveView(tenantId, {
                view: LIVE_VIEW.RECENTLY_CLOSED,
                categories: tenantCats,
                limit: QUERY_PAGE_SIZE,
              });
              if (!current()) return;
              if (fast.length) {
                const fastReady = await prepare(fast);
                if (!current()) return;
                setRecentlyClosedLiveDeals(fastReady);
                fastPainted = true;
              }
            } catch (e) {
              // A failed fast path must never cost us the full load below.
              console.warn('Recently Closed fast path failed, falling back to full load:', e?.message);
            }
          }

          // [2026-10-04] Dismissal is in a `finally`, and the duration is BOUNDED.
          //
          // As first shipped this repeated the exact defect I had just diagnosed in fetchData:
          // the toast was created with duration: Infinity and dismissed on each success/abort
          // branch, but NOT in the catch — so any throw in the fetch or in prepare() pinned
          // "Loading the rest of the market…" on screen permanently, long after the deals had
          // loaded. Tiago reported precisely that.
          //
          // Two guards, because one of them is the lesson: a `finally` means no future branch
          // added above can leak it, and a finite duration means even a pathological path
          // (unmount mid-flight, an exception thrown before finally runs) self-heals. Measured
          // for sizing: phase 2 is ~2.4s for 6,294 rows over 7 pages, and the fit hydration that
          // follows is free here because recently-expired rows carry no fit_deal_id, so
          // hydrateLiveDealsWithFit returns immediately. 15s is therefore many times the real
          // worst case rather than a guess.
          const recentToastId = fastPainted
            ? toast.loading(tr('recentlyExpired'), {
                description: tr('procLoadingRestOfMarket'),
                duration: 15000,
              })
            : null;

          try {
            const recentData = await fetchAllLiveView(tenantId, {
              view: LIVE_VIEW.RECENTLY_CLOSED,
              limit: QUERY_PAGE_SIZE,
            });
            if (!current()) return;
            const enrichedRecentWithFit = await prepare(recentData);
            if (!current()) return;
            setRecentlyClosedLiveDeals(enrichedRecentWithFit);
            writeRecentlyClosedCache(tenantId, enrichedRecentWithFit);
          } finally {
            if (recentToastId) toast.dismiss(recentToastId);
          }
        } catch (e) {
          console.warn('Recently Closed load failed:', e?.message);
        }
      })();
    } catch (err) {
      if (bootstrapToastId) toast.dismiss(bootstrapToastId);
      if (!aliveRef.current) return;
      console.error('Procurement fetch failed', err);
      setIsLoading(false);
    }
  }, [applyDealsUpdate, hydrateLiveDealsWithFit]);

  const ensureCategoriesLoaded = useCallback(async (table, newCategories, activeTabForToast = 'live') => {
    if (!table || table === 'live') return; // Live Radar / Recently Closed: client-side category chips only
    // Use tenantIdRef if available; fall back to stableTenantId.
    // Never call User.me() here — it races with mount and causes ERR_INSUFFICIENT_RESOURCES
    // when LiveRadarList is concurrently firing fit-score requests.
    const tenantId = tenantIdRef.current || stableTenantId;
    if (!tenantId) return; // prop also missing — bail cleanly
    if (!tenantIdRef.current) tenantIdRef.current = tenantId; // backfill for next caller

    const status = table === 'live' ? 'Live' : 'Closed';

    const categoriesParam = newCategories?.length ? encodeCategoriesQueryParam(newCategories) : '';
    const apiValues = newCategories?.length ? expandCategoriesForApi(newCategories) : [];
    const useAllNoFilter = newCategories.length === 0 || newCategories.length > 15;

    const cacheKey = `${tenantId}:${table}:${useAllNoFilter ? 'all' : apiValues.join('|')}`;

    let cached = categoryFetchCacheRef.current.get(cacheKey);
    if (!cached) {
      cached = readCategoryCache(tenantId, cacheKey);
      if (cached) {
        cached = cached.map(decorateDeal);
        categoryFetchCacheRef.current.set(cacheKey, cached);
      }
    }

    if (categoryFetchInProgressRef.current.has(cacheKey)) {
      return;
    }

    if (cached) {
      applyDealsUpdate((base) => mergeStatusSlice(base, cached, status), { source: `category-${table}` });
      return;
    }

    const label = table === 'live' ? tr('liveRadar') : tr('contractIntelligence');
    const toastId = toast.loading(tr('procToastLoadingMoreDeals'), {
      description: tr('procToastFetchingLabelDeals', { label }),
      duration: Infinity,
    });
    const onProgress = (page, total) => {
      toast.loading(tr('procToastLoadingMoreDeals'), {
        id: toastId,
        description: tr('procToastProcessingDealsLoaded', { n: total.toLocaleString() }),
        duration: Infinity,
      });
    };

    categoryFetchInProgressRef.current.add(cacheKey);
    setCategoryLoading(true);
    categoryFetchDoneRef.current = false;
    try {
      const result = useAllNoFilter
        ? await fetchAllNoCategories(table, tenantId, QUERY_PAGE_SIZE_FILTERED, onProgress)
        : await fetchAllForCategories(table, categoriesParam, tenantId, undefined, onProgress);
      const data = result?.items ?? result;
      const complete = result?.complete ?? true;

      const accData = await getCachedAccountList(tenantId);

      const accountMap = accData.reduce((m, a) => {
        const tid = a.tenant_id || 'unknown';
        if (a.account_id) m[`${tid}:${a.account_id}`] = a;
        if (a.account_name) m[`${tid}:${(a.account_name || '').trim()}`] = a;
        return m;
      }, {});

      const enrich = (arr, s) =>
        arr
          .map((raw) => {
            const d = normalizeDeal(raw, raw?.account) || raw;
            const tid = d?.tenant_id || raw?.tenant_id || 'unknown';

            const candidate =
              (raw?.account_id && accountMap[`${tenantId}:${raw.account_id}`]) ||
              (raw?.account_name && accountMap[`${tenantId}:${(raw.account_name || '').trim()}`]) ||
              (raw?.account_id && accountMap[`${tid}:${raw.account_id}`]) ||
              (raw?.account_name && accountMap[`${tid}:${(raw.account_name || '').trim()}`]) ||
              null;

            const dealIsGlobalLike =
              tid === 'global' || tid == null || tid === '' || tid === 'unknown';

            if (candidate && !dealIsGlobalLike && candidate.tenant_id !== tid) {
              return decorateDeal({
                ...d,
                source_status: d.status ?? raw?.status ?? null,
                account: null,
                status: s,
              });
            }

            return decorateDeal({
              ...d,
              source_status: d.status ?? raw?.status ?? null,
              account: candidate ?? null,
              status: s,
            });
          })
          .filter((d) => d && (d.id || d.deal_id || d.tender_id));

      const newDeals = enrich(data, status);
      const newDealsWithFit =
        table === 'live'
          ? await hydrateLiveDealsWithFit(tenantId, newDeals)
          : newDeals;

      if (complete && newDealsWithFit.length > 0) {
        categoryFetchCacheRef.current.set(cacheKey, newDealsWithFit);
        writeCategoryCache(tenantId, cacheKey, newDealsWithFit);
      } else if (!complete) {
        console.warn(`⚠️ Not caching incomplete data for ${cacheKey} (${newDealsWithFit.length} items) — will re-fetch next visit`);
      }

      if (!aliveRef.current) {
        toast.dismiss(toastId);
        return;
      }
      applyDealsUpdate(
        (base) => mergeStatusSlice(base, newDealsWithFit, status),
        { source: `category-${table}` }
      );
      toast.dismiss(toastId);
      pendingToastsRef.current.push({ label, status, activeTab: activeTabForToast, fetchedCount: newDealsWithFit.length });
    } catch (err) {
      toast.dismiss(toastId);
      toast.error(tr('procToastFailedLabelDeals', { label }), { description: err?.message });
      console.error('Failed to load categories:', err);
    } finally {
      categoryFetchDoneRef.current = true;
      categoryFetchInProgressRef.current.delete(cacheKey);
      setCategoryLoading(false);
    }
  }, [stableTenantId, applyDealsUpdate, hydrateLiveDealsWithFit]);

  const requestClosedCategoryFetch = useCallback((cats, activeTabForToast = 'contract') => {
    if (closedFetchDebounceRef.current) clearTimeout(closedFetchDebounceRef.current);
    closedFetchDebounceRef.current = setTimeout(() => {
      closedFetchDebounceRef.current = null;
      ensureCategoriesLoaded('closed', cats, activeTabForToast);
    }, 250);
  }, [ensureCategoriesLoaded]);

  useEffect(() => {
    return () => {
      if (closedFetchDebounceRef.current) clearTimeout(closedFetchDebounceRef.current);
    };
  }, []);

  useEffect(() => {
    if (!stableTenantId) return;
    if (lastTenantResetRef.current === stableTenantId) return;

    console.warn('[Procurement] 🔥 RESETTING PAGE STATE', {
      from: lastTenantResetRef.current,
      to: stableTenantId,
      dealsBeforeReset: dealsRef.current.length,
    });

    lastTenantResetRef.current = stableTenantId;
    tenantSessionRef.current += 1;

    const prevTenant = tenantIdRef.current || stableTenantId;
    loadVersionRef.current += 1;
    fetchDataCalledRef.current = false;
    filtersInitializedRef.current = false;
    hydratedFromCacheRef.current = false;
    tenantIdRef.current = stableTenantId;
    priorDealsForMergeRef.current = null;
    currentDealsCountRef.current = 0;
    dealsRef.current = [];
    categoryFetchCacheRef.current.clear();
    categoryFetchInProgressRef.current.clear();
    liveCategoryFilterTouchedRef.current = false;
    recentlyCategoryDefaultAppliedRef.current = false; // re-seed for the incoming tenant's categories
    _cachedAccountList = null;
    _cachedAccountListTenant = null;
    try {
      sessionStorage.removeItem(`${PROCUREMENT_LIVE_CACHE_KEY}-${prevTenant}`);
      sessionStorage.removeItem(`${PROCUREMENT_RECENTLY_CLOSED_CACHE_KEY}-${prevTenant}`);
    } catch {}
    pendingToastsRef.current = [];

    setDeals([]);
    setLiveRadarDeals([]);
    setRecentlyClosedLiveDeals([]);
    setDealAugmentByDealId({});
    recentlyDealSnapshotRef.current = new Map();
    pendingStatusDealIdsRef.current = new Set();
    setHydratedFromCache(false);
    setIsLoading(true);
    setEffectiveTenantId(stableTenantId);
    setSelectedCountries([]);
    setLiveSelectedCategories([]);
    setRecentlySelectedCategories([]);
    setClosedSelectedCategories([]);
    setInstitutionTypes([]);
    setDecisionWindows([]);
    setContractInstitutionTypes([]);
    setContractSentiments([]);
    setContractStatuses([]);
    setContractVendors([]);
    setContractProductNames([]);
    setContractResellers([]);
    setContractFrameworks([]);
    setContractExitFilter('all');
    setBenchmarkSelectedVendor(null);
  }, [stableTenantId]);

  useEffect(() => {
    if (!stableTenantId) return;

    aliveRef.current = true;
    let cancelled = false;

    const tenantId = stableTenantId;
    tenantIdRef.current = tenantId;
    setEffectiveTenantId(tenantId);

    const now = Date.now();
    const cacheHasFewer =
      currentDealsCountRef.current > 0 &&
      currentDealsCountRef.current > (_moduleCache.deals?.length || 0);

    const hasFreshModuleCache =
      _moduleCache.deals?.length &&
      _moduleCache.tenantId === tenantId &&
      _moduleCache.cachedAt &&
      now - _moduleCache.cachedAt < MODULE_CACHE_TTL;

    if (cacheHasFewer) {
      setIsLoading(false);
      return;
    }

    if (hasFreshModuleCache && !cancelled) {
      priorDealsForMergeRef.current = _moduleCache.deals;
      const d0 = _moduleCache.deals?.[0];
      const cachedDeals =
        d0 && d0._dedupeKey
          ? _moduleCache.deals
          : (_moduleCache.deals || []).map(decorateDeal);
      applyDealsUpdate(() => cachedDeals, { source: 'module-cache-hydrate' });
      if (_moduleCache.liveRadarDeals?.length) {
        setLiveRadarDeals(
          _moduleCache.liveRadarDeals[0]?._dedupeKey
            ? _moduleCache.liveRadarDeals
            : _moduleCache.liveRadarDeals.map(decorateDeal)
        );
      }
      if (_moduleCache.recentlyClosedLiveDeals?.length) {
        setRecentlyClosedLiveDeals(
          _moduleCache.recentlyClosedLiveDeals[0]?._dedupeKey
            ? _moduleCache.recentlyClosedLiveDeals
            : _moduleCache.recentlyClosedLiveDeals.map(decorateDeal)
        );
      }
      if (_moduleCache.loadedCategories) setLoadedCategories(_moduleCache.loadedCategories);
      if (_moduleCache.filterOptions) setFilterOptions(_moduleCache.filterOptions);
      if (!liveCategoryFilterTouchedRef.current) setLiveSelectedCategories([]);
      hydratedFromCacheRef.current = true;
      setHydratedFromCache(true);
      setIsLoading(false);
    }

    try {
      const raw = sessionStorage.getItem(`${PROCUREMENT_CATEGORY_CACHE_KEY}-${tenantId}`);
      if (raw) {
        const data = JSON.parse(raw);
        Object.entries(data).forEach(([key, entry]) => {
          if (entry?.deals?.length && now - (entry.cachedAt || 0) <= CATEGORY_CACHE_DURATION) {
            categoryFetchCacheRef.current.set(key, entry.deals.map(decorateDeal));
          }
        });
      }
    } catch {}

    const cached = readProcurementCache(tenantId);
    const cachedLive = readLiveCache(tenantId);

    if (!hasFreshModuleCache && !cancelled) {
      const baseDeals = (cached?.deals ?? []).map(decorateDeal);
      const startDeals = baseDeals;

      if (startDeals.length > 0 || cachedLive?.length) {
        if (startDeals.length > 0) {
          priorDealsForMergeRef.current = startDeals;
          applyDealsUpdate(() => startDeals, { source: 'session-cache-hydrate' });
        }
        if (cachedLive?.length) {
          setLiveRadarDeals(cachedLive.map(decorateDeal));
        }
        const cachedRecently = readRecentlyClosedCache(tenantId);
        if (cachedRecently?.length) {
          setRecentlyClosedLiveDeals(cachedRecently.map(decorateDeal));
        }
        if (cached?.loadedCategories) setLoadedCategories(new Set(cached.loadedCategories));
        if (cached?.filters) setFilterOptions(cached.filters);
        if (!liveCategoryFilterTouchedRef.current) setLiveSelectedCategories([]);
        hydratedFromCacheRef.current = true;
        setHydratedFromCache(true);
        setIsLoading(false);
      }
    }

    if (!cancelled && !fetchDataCalledRef.current) {
      fetchDataCalledRef.current = true;
      fetchData();
    }

    return () => {
      aliveRef.current = false;
      cancelled = true;
    };
  }, [stableTenantId]);

  useEffect(() => {
    currentDealsCountRef.current = deals.length;
    if (!tenantIdRef.current || isLoading) return;
    _moduleCache.deals = deals;
    _moduleCache.liveRadarDeals = liveRadarDeals;
    _moduleCache.recentlyClosedLiveDeals = recentlyClosedLiveDeals;
    _moduleCache.loadedCategories = new Set(loadedCategories);
    _moduleCache.filterOptions = filterOptions;
    _moduleCache.tenantId = tenantIdRef.current;
    _moduleCache.cachedAt = Date.now();
    const t = setTimeout(() => {
      const tenantId = tenantIdRef.current;
      if (!tenantId) return;
      if (deals.length > 0) {
        writeProcurementCache(tenantId, {
          deals,
          loadedCategories: Array.from(loadedCategories),
          filters: filterOptions,
        });
      }
      if (liveRadarDeals.length > 0) writeLiveCache(tenantId, liveRadarDeals);
      if (recentlyClosedLiveDeals.length > 0) writeRecentlyClosedCache(tenantId, recentlyClosedLiveDeals);
    }, 800);
    return () => clearTimeout(t);
  }, [deals, liveRadarDeals, recentlyClosedLiveDeals, loadedCategories, filterOptions, isLoading]);

  const { closedSlice } = useMemo(() => {
    const closed = [];
    for (const d of deals) {
      // is_visible is tri-state — only an explicit false hides. See procurementVisibility.js.
      if (d._statusLower !== 'live' && isDealVisible(d)) closed.push(d);
    }
    return { closedSlice: closed };
  }, [deals]);

  // BE enforces liveradar freshness (deadline + null-deadline 3-month gate). Client dedupes only.
  const liveDealsRaw = useMemo(
    () => dedupeOcdsNotices(liveRadarDeals),
    [liveRadarDeals]
  );

  const closedDealsRaw = closedSlice;

  /** Contract / Displacement / Benchmark: smowl + feedbackfruits_V2 see own closed_deals only (not global). */
  const insightTenantId = effectiveTenantId || stableTenantId;
  const ownClosedInsights = usesOwnClosedDealsForInsights(insightTenantId);
  const insightClosedDeals = useMemo(
    () => filterClosedDealsToTenantInsights(closedDealsRaw, insightTenantId),
    [closedDealsRaw, insightTenantId]
  );

  useEffect(() => {
    for (const d of recentlyClosedLiveDeals) {
      const id = resolveDealId(d);
      if (id) recentlyDealSnapshotRef.current.set(id, d);
    }
  }, [recentlyClosedLiveDeals]);

  const recentlyClosedDealsRaw = useMemo(() => {
    const base = dedupeOcdsNotices(recentlyClosedLiveDeals);
    const seen = new Set(base.map((d) => resolveDealId(d)).filter(Boolean));
    const sticky = [];
    for (const [dealId, deal] of recentlyDealSnapshotRef.current) {
      if (seen.has(dealId)) continue;
      if (dealAugmentByDealId[dealId]?.status != null) {
        sticky.push(deal);
        seen.add(dealId);
      }
    }
    return sticky.length > 0 ? [...base, ...sticky] : base;
  }, [recentlyClosedLiveDeals, dealAugmentByDealId]);

  const filterAndSearch = useCallback(
    (data, _searchFields, categoriesToUse = [], closedCategoryOnly = false) => {
      const q = debouncedSearchTerm.trim().toLowerCase();
      const countrySet = selectedCountries.length ? new Set(selectedCountries) : null;
      const itSet = institutionTypes.length ? new Set(institutionTypes) : null;
      const catSet = categoriesToUse?.length
        ? new Set(
            categoriesToUse
              .map((c) => normalizeCategoryValue(c))
              .filter(Boolean)
              .map((s) => s.toLowerCase())
          )
        : null;
      const out = [];
      for (const item of data) {
        if (q && !(item._searchBlob || '').includes(q)) continue;
        if (countrySet && !countryMatchesSelection(item.country, selectedCountries)) continue;
        if (itSet && !itSet.has(item._institutionType || '')) continue;
        if (catSet) {
          let hit = false;
          const normSet = closedCategoryOnly ? item._contractCategoryNorm : item._categoryNorm;
          for (const c of normSet || []) {
            if (catSet.has(c)) { hit = true; break; }
          }
          if (!hit) {
            const ok = closedCategoryOnly
              ? closedCategoryMatch(categoriesToUse, item)
              : categoryMatches(categoriesToUse, item);
            if (!ok) continue;
          }
        }
        out.push(item);
      }
      return out;
    },
    [debouncedSearchTerm, selectedCountries, institutionTypes]
  );

  const handleContractMapCountrySelect = useCallback((country) => {
    setSelectedCountries(country ? [country] : []);
  }, []);

  const handleMapCountryClick = useCallback((c) => {
    setMapZoomCountry((prev) => (prev === c ? null : c));
    setSelectedCountries((prev) => (prev.length === 1 && prev[0] === c ? [] : [c]));
  }, []);

  const handleClearMapZoom = useCallback(() => {
    setMapZoomCountry(null);
    setSelectedCountries([]);
  }, []);

  const liveDealsFiltered = useMemo(() => {
    let out = filterAndSearch(liveDealsRaw, ['title', 'account_name', 'category'], liveSelectedCategories);
    if (deadlineHorizon !== 'all') {
      const now = new Date();
      out = out.filter((d) => {
        if (!d.deadline) return deadlineHorizon === 'none';
        const dl = parseDateOnlyLocal(d.deadline);
        if (!dl) return deadlineHorizon === 'none';
        const days = Math.ceil((dl - now) / (24 * 60 * 60 * 1000));
        if (deadlineHorizon === '0-7') return days >= 0 && days <= 7;
        if (deadlineHorizon === '8-30') return days > 7 && days <= 30;
        if (deadlineHorizon === '31-90') return days > 30 && days <= 90;
        if (deadlineHorizon === '90+') return days > 90;
        return true;
      });
    }
    return sortLiveRadarDeals(out, liveSortConfig, liveFitOverrides, effectiveTenantId);
  }, [liveDealsRaw, filterAndSearch, deadlineHorizon, liveSortConfig, liveSelectedCategories, liveFitOverrides, effectiveTenantId]);

  const closedDealsFiltered = useMemo(() => {
    let out = filterAndSearch(closedDealsRaw, ['account_name', 'category', 'product_name', 'supplier'], closedSelectedCategories, true);
    if (decisionWindows.length > 0) {
      const bandMap = { '0-6': '0–6 months', '6-12': '6–12 months', '12-24': '12–24 months', unknown: 'Unknown' };
      const allowedBands = new Set(decisionWindows.flatMap((w) => w === 'unknown' ? ['Unknown', 'Expired'] : [bandMap[w] || w]));
      out = out.filter((d) => {
        const { band } = getDecisionWindowBandForClosedDeal(d);
        return allowedBands.has(band);
      });
    }
    // [2026-10-11] Sorting used to fall back to account_name when the sorted
    // column was empty (`a[key] ?? a.account_name`). That is not an ordering on
    // one key: sorting by Sentiment compared "university of sunderland" against
    // "neutral", so blank rows scattered through the list instead of grouping,
    // and a "—" appeared in the middle of the Neutrals. Blanks now sort last in
    // both directions, with account_name only as a tiebreaker.
    const SENTIMENT_RANK = (v) => {
      const t = String(v ?? '').trim().toLowerCase();
      if (t.startsWith('neg')) return 0;
      if (t.startsWith('neu')) return 1;
      if (t.startsWith('pos')) return 2;
      return null; // unknown -> treated as blank, sorted last
    };
    const asc = closedSortConfig.direction === 'ascending';
    const byName = (a, b) =>
      String(a.account_name || '').toLowerCase()
        .localeCompare(String(b.account_name || '').toLowerCase());

    return [...out].sort((a, b) => {
      const key = closedSortConfig.key;

      if (key === 'predicted_expiry' || key === 'predicted_expiry_date') {
        const ad = a.predicted_expiry ?? a.predicted_expiry_date;
        const bd = b.predicted_expiry ?? b.predicted_expiry_date;
        if (!ad && !bd) return byName(a, b);
        if (!ad) return 1;
        if (!bd) return -1;
        const d = new Date(ad).getTime() - new Date(bd).getTime();
        return (asc ? d : -d) || byName(a, b);
      }

      if (key === 'incumbent_sentiment') {
        const ar = SENTIMENT_RANK(a.incumbent_sentiment);
        const br = SENTIMENT_RANK(b.incumbent_sentiment);
        if (ar === null && br === null) return byName(a, b);
        if (ar === null) return 1;
        if (br === null) return -1;
        return (asc ? ar - br : br - ar) || byName(a, b);
      }

      const av = String(a[key] ?? '').trim();
      const bv = String(b[key] ?? '').trim();
      if (!av && !bv) return byName(a, b);
      if (!av) return 1;
      if (!bv) return -1;
      const cmp = av.toLowerCase().localeCompare(bv.toLowerCase());
      return (asc ? cmp : -cmp) || byName(a, b);
    });
  }, [closedDealsRaw, filterAndSearch, decisionWindows, closedSortConfig, closedSelectedCategories]);

  const recentlyClosedDealsFiltered = useMemo(() => {
    let out = filterAndSearch(recentlyClosedDealsRaw, ['title', 'account_name', 'category'], recentlySelectedCategories, false);
    return sortLiveRadarDeals(out, liveSortConfig, liveFitOverrides, effectiveTenantId);
  }, [recentlyClosedDealsRaw, filterAndSearch, recentlySelectedCategories, liveSortConfig, liveFitOverrides, effectiveTenantId]);

  const contractClosedDealsFiltered = useMemo(() => {
    if (activeTab !== 'contract') return [];
    const q = debouncedSearchTerm.toLowerCase().trim();
    const countriesActive = selectedCountries.length > 0;
    const hasCategoryFilter = closedSelectedCategories.length > 0;
    const vendorSet = contractVendors.length ? new Set(contractVendors) : null;
    const productNameSet = contractProductNames.length ? new Set(contractProductNames) : null;
    const resellerSet = contractResellers.length ? new Set(contractResellers) : null;
    const frameworkSet = contractFrameworks.length ? new Set(contractFrameworks) : null;
    const institutionTypeSet = contractInstitutionTypes.length ? new Set(contractInstitutionTypes) : null;
    const sentimentSet = contractSentiments.length ? new Set(contractSentiments) : null;
    const statusSet = contractStatuses.length ? new Set(contractStatuses) : null;
    const bandMap = { '0-6': '0–6 months', '6-12': '6–12 months', '12-24': '12–24 months', unknown: 'Unknown' };
    const allowedBands = decisionWindows.length > 0
      ? new Set(decisionWindows.flatMap((w) => w === 'unknown' ? ['Unknown', 'Expired'] : [bandMap[w] || w]))
      : null;

    // Exclude Proctorio from LMS results — Proctorio is a proctoring vendor, not LMS
    const isLmsSelected = closedSelectedCategories.some((c) => {
      const norm = toOfficialCategoryName(c) || c;
      return norm === CATEGORY_KEY_TO_OFFICIAL.lms;
    });

    const out = [];
    for (const d of insightClosedDeals) {
      if (q && !(d._searchBlob || '').includes(q)) continue;
      if (countriesActive && !countryMatchesSelection(d.country, selectedCountries)) continue;
      if (hasCategoryFilter && !closedCategoryMatch(closedSelectedCategories, d)) continue;
      if (vendorSet) {
        if (!d._supplierCanonical || !vendorSet.has(d._supplierCanonical)) continue;
      }
      if (productNameSet) {
        const productName = (d.product_name || '').trim();
        if (!productNameSet.has(productName)) continue;
      }
      if (resellerSet) {
        const reseller = (d.reseller || '').trim();
        if (!resellerSet.has(reseller)) continue;
      }
      if (frameworkSet && !frameworkNamesOf(d).some((n) => frameworkSet.has(n))) continue;
      if (isLmsSelected && (d._supplierLower || '') === 'proctorio') continue;
      if (institutionTypeSet && !institutionTypeSet.has(d._institutionType || '')) continue;
      // [2026-10-17] This status check used to sit INSIDE the `if (sentimentSet)` block below, so
      // Status only filtered anything when a Sentiment was ALSO selected. On its own it was a
      // no-op — Status=Active still listed Expired rows. It is a filter in its own right.
      if (statusSet && !statusSet.has(d.status)) continue;
      if (sentimentSet) {
        const raw = d.incumbent_sentiment ?? d.account?.incumbent_sentiment;
        const sent = canonicalizeSentiment(raw == null || raw === '' ? 'unknown' : raw);
        if (!sentimentSet.has(sent)) continue;
      }
      if (allowedBands) {
        const { band } = getDecisionWindowBandForClosedDeal(d);
        if (!allowedBands.has(band)) continue;
      }
      out.push(d);
    }

    return [...out].sort((a, b) => {
      const aDate = a.predicted_expiry ?? a.predicted_expiry_date;
      const bDate = b.predicted_expiry ?? b.predicted_expiry_date;
      const ad = aDate ? new Date(aDate).getTime() : 0;
      const bd = bDate ? new Date(bDate).getTime() : 0;
      return ad - bd;
    });
  }, [activeTab, insightClosedDeals, debouncedSearchTerm, selectedCountries, closedSelectedCategories, contractInstitutionTypes, contractSentiments, contractStatuses, contractVendors, contractProductNames, contractResellers, contractFrameworks, decisionWindows]);

  const competitorControlledFilters = useMemo(
    () => ({
      categories: closedSelectedCategories,
      countries: selectedCountries,
      institutionTypes: contractInstitutionTypes,
      statuses: contractStatuses,
      sentiments: contractSentiments,
      vendors: contractVendors,
      productNames: contractProductNames,
      resellers: contractResellers,
      frameworks: contractFrameworks,
      decisionWindows,
      exitFilter: contractExitFilter,
    }),
    [
      closedSelectedCategories,
      selectedCountries,
      contractInstitutionTypes,
      contractStatuses,
      contractSentiments,
      contractVendors,
      contractProductNames,
      contractResellers,
      contractFrameworks,
      decisionWindows,
      contractExitFilter,
    ],
  );

  // Show deferred toasts with both fetched and visible counts
  useEffect(() => {
    const pending = pendingToastsRef.current;
    if (!pending?.length || isLoading) return;
    pendingToastsRef.current = [];
    pending.forEach(({ label, status, activeTab: tab, fetchedCount }) => {
      const visibleCount =
        status === 'live'
          ? (tab === 'recently' ? recentlyClosedDealsFiltered.length : liveDealsFiltered.length)
          : contractClosedDealsFiltered.length;
      const fetched = fetchedCount ?? visibleCount;
      toast.success(tr('procToastLabelReady', { label: label || '' }), {
        description: tr('procToastFetchedVisible', { fetched: fetched.toLocaleString(), visible: visibleCount.toLocaleString() }),
      });
    });
  }, [deals, liveDealsFiltered.length, recentlyClosedDealsFiltered.length, contractClosedDealsFiltered.length, isLoading]);

  const serverTabSuppliers = useMemo(() => {
    if (activeTab === 'benchmark' && benchmarkSelectedVendor) {
      return [benchmarkSelectedVendor];
    }
    return contractVendors.length ? contractVendors : [];
  }, [activeTab, benchmarkSelectedVendor, contractVendors]);

  // Used by server-backed tabs; must be declared before the hook call below.
  const tenantId = effectiveTenantId || tenantIdRef.current || currentTenant || 'instructure';
  const contractIntelligenceTenantId = effectiveTenantId || tenantIdRef.current || currentTenant || null;

  const procurementServerTabs = useProcurementServerTabs({
    tenantId,
    activeTab,
    categories: closedSelectedCategories,
    countries: selectedCountries,
    institutionTypes: contractInstitutionTypes,
    suppliers: serverTabSuppliers,
    search: debouncedSearchTerm,
    ownClosedDealsOnly: ownClosedInsights,
  });

  const insightClosedDealsForCharts = useMemo(() => {
    if (!ownClosedInsights) return [];
    const q = debouncedSearchTerm.toLowerCase().trim();
    const countriesActive = selectedCountries.length > 0;
    const hasCategoryFilter = closedSelectedCategories.length > 0;
    const vendorSet = contractVendors.length ? new Set(contractVendors) : null;
    const institutionTypeSet = contractInstitutionTypes.length ? new Set(contractInstitutionTypes) : null;
    return insightClosedDeals.filter((d) => {
      if (q && !(d._searchBlob || '').includes(q)) return false;
      if (countriesActive && !countryMatchesSelection(d.country, selectedCountries)) return false;
      if (hasCategoryFilter && !closedCategoryMatch(closedSelectedCategories, d)) return false;
      if (vendorSet && (!d._supplierCanonical || !vendorSet.has(d._supplierCanonical))) return false;
      if (institutionTypeSet && !institutionTypeSet.has(d._institutionType || '')) return false;
      return true;
    });
  }, [
    ownClosedInsights,
    insightClosedDeals,
    debouncedSearchTerm,
    selectedCountries,
    closedSelectedCategories,
    contractVendors,
    contractInstitutionTypes,
  ]);


  const deferredLiveDealsFiltered = useDeferredValue(liveDealsFiltered);
  const deferredRecentlyClosedDealsFiltered = useDeferredValue(recentlyClosedDealsFiltered);
  const deferredClosedDealsFiltered = useDeferredValue(closedDealsFiltered);

  const mapDeals = activeTab === 'live' ? liveDealsFiltered : closedDealsFiltered;

  const { uniqueCategories, uniqueInstitutionTypes, uniqueCountries } = useMemo(() => {
    const cats = new Set();
    const types = new Set();
    const countries = new Set();
    // [2026-10-17] getDealOptionCategories, NOT getDealCategories. This set becomes the Category
    // filter's option list, and the union now includes `matched_categories`, which is uncontrolled
    // free text — measured on the 9,224 live rows, the dropdown went 70 -> 414 entries, the extras
    // being raw keys, vendor names and whole sentences. Only the OPTION list is narrowed; the
    // filter still matches on the full union via _categoryNorm, so bundled deals stay retrievable
    // under every canonical category they carry.
    for (const d of [...liveRadarDeals, ...recentlyClosedLiveDeals]) {
      getDealOptionCategories(d).forEach((c) => cats.add(c));
    }
    for (const d of deals) {
      getDealOptionCategories(d).forEach((c) => cats.add(c));
      if (d._institutionType) types.add(d._institutionType);
      if (d._countryDisplay) countries.add(d._countryDisplay);
    }
    return {
      uniqueCategories: Array.from(cats).sort(),
      uniqueInstitutionTypes: Array.from(types).filter(Boolean).sort(),
      uniqueCountries: Array.from(countries).filter(Boolean).sort(),
    };
  }, [deals, liveRadarDeals, recentlyClosedLiveDeals]);

  const categoryOptions = useMemo(
    () => buildProcurementCategoryOptions(filterOptions.categories, uniqueCategories),
    [filterOptions.categories, uniqueCategories],
  );

  const countryOptions = useMemo(
    () => (filterOptions.countries?.length ? filterOptions.countries : uniqueCountries),
    [filterOptions.countries, uniqueCountries]
  );

  /**
   * [2026-10-17] Live Radar / Recently Expired institution-type filter options.
   * Same precedence as countryOptions: the bootstrap's filter list, else the set derived
   * from loaded rows. `institutionTypes` state and the `filterAndSearch` predicate that
   * reads it already existed — only the control was missing, which is why
   * uniqueInstitutionTypes was computed and never used.
   */
  const institutionTypeOptions = useMemo(
    () => (filterOptions.institutionTypes?.length ? filterOptions.institutionTypes : uniqueInstitutionTypes),
    [filterOptions.institutionTypes, uniqueInstitutionTypes]
  );

  /** Tenant default categories — visual hint on Live Radar chips only; never auto-filters the load. */
  const preferredLiveCategories = useMemo(() => {
    const tid = effectiveTenantId || stableTenantId;
    if (!tid) return [];
    return getDealCategoriesForTenant(tid).filter((c) => categoryOptions.includes(c));
  }, [effectiveTenantId, stableTenantId, categoryOptions]);

  const myTendersTotalCount = useMemo(() => {
    if (!myTendersConfig?.enabled || !preferredLiveCategories?.length) return 0;
    const ids = new Set();
    for (const d of [...liveDealsFiltered, ...recentlyClosedDealsFiltered]) {
      if (!dealMatchesPrimaryProcurementCategories(d, preferredLiveCategories)) continue;
      const id = resolveDealId(d);
      if (id) ids.add(id);
    }
    return ids.size;
  }, [
    myTendersConfig?.enabled,
    preferredLiveCategories,
    liveDealsFiltered,
    recentlyClosedDealsFiltered,
  ]);

  useEffect(() => {
    const tid = stableTenantId || effectiveTenantId;
    if (!tid) return;
    fetchProductProfiles(tid)
      .then((list) => {
        setActiveProductProfiles((list || []).filter((p) => p.is_active));
      })
      .catch(() => setActiveProductProfiles([]));
  }, [stableTenantId, effectiveTenantId]);

  useEffect(() => {
    const tid = effectiveTenantId || stableTenantId;
    if (!tid) {
      setMyTendersConfig({ enabled: false });
      return;
    }
    let cancelled = false;
    fetchMyTendersConfig(tid).then((cfg) => {
      if (!cancelled) setMyTendersConfig(cfg?.enabled ? cfg : { enabled: false });
    });
    return () => {
      cancelled = true;
    };
  }, [effectiveTenantId, stableTenantId]);

  const myTendersAugmentDealIds = useMemo(() => {
    if (!myTendersConfig?.enabled) return [];
    const ids = new Set([
      ...collectDealIds(liveRadarDeals),
      ...collectDealIds(recentlyClosedLiveDeals),
      ...collectDealIds(recentlyClosedDealsRaw),
    ]);
    return Array.from(ids).sort();
  }, [myTendersConfig?.enabled, liveRadarDeals, recentlyClosedLiveDeals, recentlyClosedDealsRaw]);

  useEffect(() => {
    if (!myTendersConfig?.enabled || !tenantId || myTendersAugmentDealIds.length === 0) {
      if (!myTendersConfig?.enabled) {
        setDealAugmentByDealId({});
        setDealAugmentError(false);
      }
      return;
    }
    let cancelled = false;
    fetchMyTendersAugment(tenantId, myTendersAugmentDealIds)
      .then((records) => {
        if (cancelled) return;
        setDealAugmentError(false);
        setDealAugmentByDealId((prev) =>
          mergeAugmentRecords(prev, records, {
            preserveDealIds: pendingStatusDealIdsRef.current,
          })
        );
      })
      .catch((e) => {
        if (cancelled) return;
        // Keep last-known augment; never paint every row as Untouched on failure.
        setDealAugmentError(true);
        console.warn('[MyTenders] augment failed', e);
      });
    return () => {
      cancelled = true;
    };
  }, [myTendersConfig?.enabled, tenantId, myTendersAugmentDealIds.join(',')]);

  const handleDealStatusChange = useCallback(
    async (dealId, status, note) => {
      if (!tenantId || !dealId) return;
      const canonicalId = String(dealId);
      const userName =
        contextUser?.full_name ||
        contextUser?.name ||
        [contextUser?.first_name, contextUser?.last_name].filter(Boolean).join(' ') ||
        null;
      let previous;
      const now = new Date().toISOString();

      const recentDeal = recentlyClosedLiveDeals.find((d) => resolveDealId(d) === canonicalId);
      if (recentDeal) {
        recentlyDealSnapshotRef.current.set(canonicalId, recentDeal);
      }

      const clearing = status === 'untouched';

      pendingStatusDealIdsRef.current.add(canonicalId);
      setDealAugmentByDealId((map) => {
        previous = map[canonicalId] || {};
        if (clearing) {
          return {
            ...map,
            [canonicalId]: {
              ...previous,
              status: null,
              status_set_at: null,
              status_set_by_user_name: null,
              status_set_by_user_id: null,
            },
          };
        }
        return {
          ...map,
          [canonicalId]: {
            ...previous,
            status,
            status_set_at: now,
            ...(userName
              ? {
                  status_set_by_user_name: userName,
                  status_set_by_user_id: contextUser?.id ?? previous.status_set_by_user_id,
                }
              : {}),
          },
        };
      });

      try {
        if (clearing) {
          await clearDealStatus(tenantId, canonicalId);
          setDealAugmentByDealId((map) => ({
            ...map,
            [canonicalId]: {
              ...(map[canonicalId] || {}),
              status: null,
              status_set_at: null,
            },
          }));
        } else {
          const result = await setDealStatus(tenantId, canonicalId, status, note);
          setDealAugmentByDealId((map) => ({
            ...map,
            [canonicalId]: {
              ...(map[canonicalId] || {}),
              status: result.to_status ?? status,
              status_set_at: result.set_at ?? map[canonicalId]?.status_set_at ?? now,
              ...(userName
                ? {
                    status_set_by_user_name: userName,
                    status_set_by_user_id: contextUser?.id ?? map[canonicalId]?.status_set_by_user_id,
                  }
                : {}),
            },
          }));
        }
        myTendersTabRef.current?.refetchSummary();
      } catch (e) {
        setDealAugmentByDealId((map) => ({ ...map, [canonicalId]: previous }));
        toast.error(e?.message || (clearing ? tr('procToastStatusClearFailed') : tr('procToastStatusUpdateFailed')));
        throw e;
      } finally {
        pendingStatusDealIdsRef.current.delete(canonicalId);
      }
    },
    [tenantId, contextUser, recentlyClosedLiveDeals]
  );

  const handleDealValueOverrideChange = useCallback(
    async (dealId, newValue) => {
      if (!tenantId || !dealId) return;
      const canonicalId = String(dealId);
      const userName = contextUser?.full_name || contextUser?.name || null;
      let previous;
      const now = new Date().toISOString();

      pendingStatusDealIdsRef.current.add(canonicalId);
      setDealAugmentByDealId((map) => {
        previous = map[canonicalId] || {};
        return {
          ...map,
          [canonicalId]: {
            ...previous,
            value_override: newValue,
            value_override_set_at: newValue == null ? null : now,
            value_override_by_user_name: newValue == null ? null : userName,
          },
        };
      });

      try {
        if (newValue == null) {
          await clearDealValueOverride(tenantId, canonicalId);
        } else {
          await setDealValueOverride(tenantId, canonicalId, newValue);
        }
        myTendersTabRef.current?.refetchSummary();
      } catch (e) {
        setDealAugmentByDealId((map) => ({ ...map, [canonicalId]: previous }));
        toast.error(e?.message || tr('procToastValueOverrideFailed'));
        throw e;
      } finally {
        pendingStatusDealIdsRef.current.delete(canonicalId);
      }
    },
    [tenantId, contextUser]
  );

  const handleDealViewLog = useCallback(
    (dealId) => {
      if (!myTendersConfig?.enabled || !tenantId || !dealId) return;
      logDealView(tenantId, dealId);
    },
    [myTendersConfig?.enabled, tenantId]
  );

  const dealStatusColumnActive =
    myTendersConfig?.enabled &&
    !(dealAugmentError && Object.keys(dealAugmentByDealId).length === 0);
  useEffect(() => {
    if (filtersInitializedRef.current) return;
    if (!effectiveTenantId) return; // don't run until tenant is resolved
    if (categoryOptions.length > 0 || countryOptions.length > 0) {
      const tenantCats = getDealCategoriesForTenant(effectiveTenantId).filter((c) => categoryOptions.includes(c));
      filtersInitializedRef.current = true;
      setSelectedCountries([]);
      setLiveSelectedCategories([]);
      setClosedSelectedCategories(tenantCats.length > 0 ? tenantCats : []);
      // Ensure data is loaded for auto-selected categories
      if (tenantCats.length > 0) {
        tenantIdRef.current = effectiveTenantId; // force ref before call
        requestClosedCategoryFetch(tenantCats, 'contract');
      }
    }
  }, [categoryOptions, countryOptions, effectiveTenantId, requestClosedCategoryFetch]);

  /**
   * Recently Expired: default the category filter to the tenant's categories on first open.
   *
   * Mirrors the Contract Intelligence default above, with one difference that matters — Contract
   * fetches closed_deals per category (requestClosedCategoryFetch), whereas the Recently Expired
   * rows are ALREADY loaded in recentlyClosedLiveDeals and filterAndSearch filters them in the
   * client. So seeding the selection is the whole change; no fetch is required or wanted here.
   *
   * preferredLiveCategories is getDealCategoriesForTenant(tenant) narrowed to categories that exist
   * in the loaded data, which is the identical expression Contract Intelligence uses for its own
   * default. If the tenant has no mapped categories it stays empty, i.e. unchanged "all" behaviour.
   *
   * 🔴 Deliberately NOT gated on activeTab === 'recently'. It was, and the tab label renders
   * recentlyClosedDealsFiltered.length — so before the tab had ever been opened the selection was
   * still [] and the label showed the UNFILTERED total, then dropped to the category-filtered
   * number the instant the tab was clicked and this effect finally ran. Seeding is pure client-side
   * state (see above: no fetch), so there is nothing to defer and the label is now correct on load.
   */
  useEffect(() => {
    if (recentlyCategoryDefaultAppliedRef.current) return;
    if (!preferredLiveCategories.length) return;
    recentlyCategoryDefaultAppliedRef.current = true;
    // getDealCategoriesForTenant de-duplicates at source as of 2026-10-04; the Set here is belt-and-
    // braces so a duplicate reintroduced upstream can never inflate the "(n)" category badge.
    setRecentlySelectedCategories([...new Set(preferredLiveCategories)]);
  }, [preferredLiveCategories]);

  const contractInstitutionTypeOptions = useMemo(() => {
    const isContractTab =
      activeTab === 'contract' || activeTab === 'displacement' || activeTab === 'benchmark';
    if (!isContractTab) return [];
    const set = new Set();
    insightClosedDeals.forEach((d) => {
      const it = d._institutionType || '';
      if (it) set.add(it);
    });
    return Array.from(set).filter(Boolean).sort();
  }, [activeTab, insightClosedDeals]);
  const contractVendorOptions = useMemo(() => {
    const isContractTab =
      activeTab === 'contract' || activeTab === 'displacement' || activeTab === 'benchmark';
    if (!isContractTab) return [];
    const cats = closedSelectedCategories.length ? closedSelectedCategories : categoryOptions;
    const internal = dealStringsToInternalCategories(cats);
    const fromDefinitions = getVendorsForCategories(internal.length ? internal : getCategoriesForTenant(tenantId), tenantId, true);
    const fromDeals = new Set();
    insightClosedDeals.forEach((d) => {
      const catMatch = !cats.length || closedCategoryMatch(cats, d);
      if (!catMatch) return;
      if (d._supplierCanonical) fromDeals.add(d._supplierCanonical);
    });
    const merged = new Set([...fromDefinitions, ...fromDeals]);
    return Array.from(merged).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }, [activeTab, closedSelectedCategories, categoryOptions, tenantId, insightClosedDeals]);

  const contractProductNameOptions = useMemo(() => {
    if (activeTab !== 'contract') return [];
    const cats = closedSelectedCategories.length ? closedSelectedCategories : categoryOptions;
    const set = new Set();
    insightClosedDeals.forEach((d) => {
      const catMatch = !cats.length || closedCategoryMatch(cats, d);
      if (!catMatch) return;
      const productName = (d.product_name || '').trim();
      if (productName) set.add(productName);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }, [activeTab, closedSelectedCategories, categoryOptions, insightClosedDeals]);

  const contractResellerOptions = useMemo(() => {
    if (activeTab !== 'contract') return [];
    const cats = closedSelectedCategories.length ? closedSelectedCategories : categoryOptions;
    const set = new Set();
    insightClosedDeals.forEach((d) => {
      const catMatch = !cats.length || closedCategoryMatch(cats, d);
      if (!catMatch) return;
      const reseller = (d.reseller || '').trim();
      if (reseller) set.add(reseller);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }, [activeTab, closedSelectedCategories, categoryOptions, insightClosedDeals]);

  const contractFrameworkOptions = useMemo(() => {
    if (activeTab !== 'contract') return [];
    const cats = closedSelectedCategories.length ? closedSelectedCategories : categoryOptions;
    const set = new Set();
    insightClosedDeals.forEach((d) => {
      const catMatch = !cats.length || closedCategoryMatch(cats, d);
      if (!catMatch) return;
      frameworkNamesOf(d).forEach((n) => set.add(n));
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }, [activeTab, closedSelectedCategories, categoryOptions, insightClosedDeals]);

  const benchmarkVendorOptions = useMemo(() => {
    if (activeTab !== 'benchmark') return [];
    if (closedSelectedCategories.length === 0) return [];
    const set = new Set();

    insightClosedDeals.forEach((d) => {
      if (!closedCategoryMatch(closedSelectedCategories, d)) return;

      const label = toBenchmarkVendorLabel(d);
      if (label) set.add(label);
    });

    return Array.from(set).sort();
  }, [activeTab, insightClosedDeals, closedSelectedCategories]);

  /**
   * [2026-10-17] The Institution type control is shown only on Live Radar / Recently Expired,
   * because Contract Intelligence has its own (contractInstitutionTypes) and two controls for one
   * concept on the same screen is confusing. But `institutionTypes` is read by filterAndSearch for
   * the CLOSED slice too, so hiding the control without clearing the state would leave a filter
   * applied on a tab with nothing to see or clear it by — a silent empty result. Clear it instead.
   */
  useEffect(() => {
    if (activeTab === 'live' || activeTab === 'recently') return;
    setInstitutionTypes((prev) => (prev.length ? [] : prev));
  }, [activeTab]);

  useEffect(() => {
    if (activeTab !== 'benchmark') return;
    if (benchmarkSelectedVendor && benchmarkVendorOptions.length > 0 && !benchmarkVendorOptions.includes(benchmarkSelectedVendor)) {
      setBenchmarkSelectedVendor(null);
    }
  }, [activeTab, benchmarkVendorOptions, benchmarkSelectedVendor]);

  const showSkeletons = isLoading && !hydratedFromCache;
  if (selectedClosedDeal) {
    return (
      <div className="-m-8 -mt-8 min-h-screen">
        <ClosedDealDetailPage
          key={resolveDealId(selectedClosedDeal) || 'deal'}
          deal={selectedClosedDeal}
          onBack={closeClosedDeal}
          tenantId={tenantId}
          logViews={myTendersConfig?.enabled}
          peerDeals={insightClosedDeals}
        />
      </div>
    );
  }

  return (
    <div className="bg-gray-50 -m-8 -mt-8 min-h-screen">
      <div className="bg-white border-b mb-4 -mx-8">
        <div className="max-w-7xl mx-auto px-12 sm:px-8 md:px-10 py-5">
          <h1 className="text-2xl font-bold text-[#0A1A2F]">{t('dealsTenders') || 'Procurement Intelligence'}</h1>
          <p className="text-sm text-[#5C6B7A]">{t('trackTenders') || 'Track live tenders and closed awards'}</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-2 sm:px-6 py-0">
        {showSkeletons ? (
          <div className="space-y-4">
            <div className="h-10 bg-gray-100 rounded-md animate-pulse" />
            <div className="h-64 bg-gray-100 rounded-md animate-pulse" />
          </div>
        ) : (
          <>
            <div className="sticky top-0 z-20 bg-white/95 backdrop-blur border-b py-2 mb-3 -mx-2 px-2">
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <div className="relative flex-1 min-w-[200px]">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <Input
                    placeholder={t('searchKeyword') || 'Search...'}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
                {activeTab !== 'recently' && activeTab !== 'benchmark' && activeTab !== 'displacement' && (
                <Button
                  variant={mapVisible ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setMapVisible(!mapVisible)}
                >
                  {mapVisible ? <List className="w-4 h-4 mr-1" /> : <MapIcon className="w-4 h-4 mr-1" />}
                  {mapVisible ? t('listOnly') : t('mapPlusList')}
                </Button>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <PopoverStaysOpen open={openFilter === 'country'} onOpenChange={(o) => setOpenFilter(o ? 'country' : null)}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" className="h-8 text-sm">
                      {t('country')} {selectedCountries.length ? `(${selectedCountries.length})` : ''}
                      <ChevronDown className="w-4 h-4 ml-1" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="max-h-60 overflow-y-auto pt-0" align="start">
                    <div className="space-y-2 py-1">
                      {/* [2026-10-17] Sticky header. These popovers scroll (Country alone is 130 options, 3,757px of content in a 239px box) and the Select all / Clear all buttons and the search box sat INSIDE the scroll area, so one screen of scrolling put them out of reach - they read as buttons that do nothing. The container's top padding is removed (pt-0) because `sticky top-0` pins to the PADDING-BOX edge: with p-4 the pin sat 16px low and rows scrolled through that strip above the header. */}
                      <div className="sticky top-0 z-10 bg-popover pt-4">
                        <div className="flex flex-wrap gap-2 pb-2 border-b text-xs">
                          <button
                            type="button"
                            className="px-2 py-1.5 rounded bg-gray-100 hover:bg-gray-200 text-[#00C2C2] font-medium"
                            onClick={() => setSelectedCountries([...countryOptions])}
                          >
                            {t('selectAll')}
                          </button>
                          <button
                            type="button"
                            className="px-2 py-1.5 rounded bg-gray-100 hover:bg-gray-200 text-gray-600 font-medium"
                            onClick={() => setSelectedCountries([])}
                          >
                            {t('clearAll')}
                          </button>
                        </div>
                        <Input
                          placeholder={t('typeToSearch')}
                          value={countryFilterQuery}
                          onChange={(e) => setCountryFilterQuery(e.target.value)}
                          className="h-8 text-sm mb-1"
                          onKeyDown={(e) => e.stopPropagation()}
                        />
                      </div>
                      {countryOptions
                        .filter((c) => !countryFilterQuery.trim() || c.toLowerCase().includes(countryFilterQuery.trim().toLowerCase()))
                        .map((c) => (
                        <label key={c} className="flex items-center gap-2 cursor-pointer">
                          <Checkbox
                            checked={selectedCountries.includes(c)}
                            onCheckedChange={(checked) => {
                              setSelectedCountries((prev) =>
                                checked ? [...prev, c] : prev.filter((x) => x !== c)
                              );
                            }}
                          />
                          <span className="text-sm">{c}</span>
                        </label>
                      ))}
                    </div>
                  </PopoverContent>
                </PopoverStaysOpen>
                <PopoverStaysOpen open={openFilter === 'category'} onOpenChange={(o) => setOpenFilter(o ? 'category' : null)}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" className="h-8 text-sm">
                      {/* [2026-10-17] Tiago's call: the teal "all" badge came off. It was the only
                          filter chip in the bar that rendered a state word, so Category read as a
                          different KIND of control from its ten neighbours. Every chip now shows a
                          count or nothing. The behaviour is untouched - empty still means every
                          category on Live Radar and Recently Expired. */}
                      {t('category')}{' '}
                      {activeTab === 'live'
                        ? (liveSelectedCategories.length ? `(${liveSelectedCategories.length})` : '')
                        : activeTab === 'recently'
                          ? (recentlySelectedCategories.length ? `(${recentlySelectedCategories.length})` : '')
                          : (closedSelectedCategories.length ? `(${closedSelectedCategories.length})` : '')}
                      <ChevronDown className="w-4 h-4 ml-1" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="max-h-60 overflow-y-auto pt-0" align="start" >
                    <div className="space-y-2 py-1">
                      {/* [2026-10-17] Sticky header. These popovers scroll (Country alone is 130 options, 3,757px of content in a 239px box) and the Select all / Clear all buttons and the search box sat INSIDE the scroll area, so one screen of scrolling put them out of reach - they read as buttons that do nothing. The container's top padding is removed (pt-0) because `sticky top-0` pins to the PADDING-BOX edge: with p-4 the pin sat 16px low and rows scrolled through that strip above the header. */}
                      <div className="sticky top-0 z-10 bg-popover pt-4">
                        <div className="flex flex-wrap gap-2 pb-2 border-b text-xs">
                          <button
                            type="button"
                            className="px-2 py-1.5 rounded bg-gray-100 hover:bg-gray-200 text-[#00C2C2] font-medium"
                            onClick={() => {
                              // [2026-10-17] Select all SELECTS ALL, on every tab. On Live Radar and
                              // Recently Expired this button used to CLEAR the selection, on the
                              // reasoning that an empty set already means "every category" — true for
                              // the filter, but it meant a button labelled "Select all" could never
                              // tick a single box, which is what Tiago reported. The touched/applied
                              // refs are still set so a tenant default cannot re-seed over a choice
                              // made here.
                              if (activeTab === 'live') {
                                liveCategoryFilterTouchedRef.current = true;
                                setLiveSelectedCategories([...categoryOptions]);
                              } else if (activeTab === 'recently') {
                                recentlyCategoryDefaultAppliedRef.current = true;
                                setRecentlySelectedCategories([...categoryOptions]);
                              } else {
                                // [2026-10-17] Select all no longer fires the per-category client
                                // bulk load. Measured on Contract Intelligence: it ticked all 89
                                // boxes, then fired ~89 sequential /query loads that starved the main
                                // thread - the table went empty, no /contract-table request was ever
                                // issued, and the renderer stopped responding entirely. This tab is
                                // server-backed (useServerAggregates is always true here), so the
                                // table and map come from /aggregate + /contract-table, which handle
                                // the full category list in one request each. Ticking categories
                                // individually keeps the old client load - only the all-at-once case,
                                // which is the only one large enough to freeze the page, skips it.
                                setClosedSelectedCategories([...categoryOptions]);
                              }
                            }}
                          >
                            {t('selectAll')}
                          </button>
                          <button
                            type="button"
                            className="px-2 py-1.5 rounded bg-gray-100 hover:bg-gray-200 text-gray-600 font-medium"
                            onClick={() => {
                              if (activeTab === 'live') {
                                setLiveSelectedCategories([]);
                              } else if (activeTab === 'recently') {
                                // "Clear all" is a deliberate choice — stop the tenant default re-seeding it.
                                recentlyCategoryDefaultAppliedRef.current = true;
                                setRecentlySelectedCategories([]);
                              } else {
                                setClosedSelectedCategories([]);
                              }
                            }}
                          >
                            {t('clearAll')}
                          </button>
                        </div>
                        <Input
                          placeholder={t('typeToSearch')}
                          value={categoryFilterQuery}
                          onChange={(e) => setCategoryFilterQuery(e.target.value)}
                          className="h-8 text-sm mb-1"
                          onKeyDown={(e) => e.stopPropagation()}
                        />
                      </div>
                      {categoryOptions
                        .filter((c) => !categoryFilterQuery.trim() || c.toLowerCase().includes(categoryFilterQuery.trim().toLowerCase()))
                        .map((c) => {
                        const isLive = activeTab === 'live';
                        const isRecently = activeTab === 'recently';
                        const selected = isLive
                          ? liveSelectedCategories.includes(c)
                          : isRecently
                            ? recentlySelectedCategories.includes(c)
                            : closedSelectedCategories.includes(c);
                        const isPreferred = isLive && preferredLiveCategories.includes(c) && !selected;
                        return (
                          <label
                            key={c}
                            className={`flex items-center gap-2 cursor-pointer rounded px-1 -mx-1 ${isPreferred ? 'bg-teal-50/70 ring-1 ring-teal-200/80' : ''}`}
                          >
                            <Checkbox
                              checked={selected}
                              onCheckedChange={(checked) => {
                                if (activeTab === 'live') {
                                  liveCategoryFilterTouchedRef.current = true;
                                  if (checked) {
                                    setLiveSelectedCategories((prev) => [...prev, c]);
                                  } else {
                                    setLiveSelectedCategories((prev) => prev.filter((x) => x !== c));
                                  }
                                } else if (activeTab === 'recently') {
                                  recentlyCategoryDefaultAppliedRef.current = true;
                                  if (checked) {
                                    setRecentlySelectedCategories((prev) => [...prev, c]);
                                  } else {
                                    setRecentlySelectedCategories((prev) => prev.filter((x) => x !== c));
                                  }
                                } else {
                                  if (checked) {
                                    const newCats = [...closedSelectedCategories, c];
                                    requestClosedCategoryFetch(newCats, 'contract');
                                    setClosedSelectedCategories(newCats);
                                  } else {
                                    setClosedSelectedCategories((prev) => prev.filter((x) => x !== c));
                                  }
                                }
                              }}
                            />
                            <span className={`text-sm ${isPreferred ? 'text-teal-800' : ''}`}>{c}</span>
                          </label>
                        );
                      })}
                    </div>
                  </PopoverContent>
                </PopoverStaysOpen>
                {(activeTab === 'live' || activeTab === 'recently') && (
                  <PopoverStaysOpen open={openFilter === 'liveInstitutionType'} onOpenChange={(o) => setOpenFilter(o ? 'liveInstitutionType' : null)}>
                    <PopoverTrigger asChild>
                      <Button variant="outline" size="sm" className="h-8 text-sm">
                        {t('institutionType')} {institutionTypes.length ? `(${institutionTypes.length})` : ''}
                        <ChevronDown className="w-4 h-4 ml-1" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-56 max-h-60 overflow-y-auto" align="start">
                      <div className="flex flex-wrap gap-2 pb-2 border-b text-xs mb-2">
                        <button type="button" className="px-2 py-1.5 rounded bg-gray-100 hover:bg-gray-200 text-[#00C2C2] font-medium" onClick={() => setInstitutionTypes([...institutionTypeOptions])}>{t('selectAll')}</button>
                        <button type="button" className="px-2 py-1.5 rounded bg-gray-100 hover:bg-gray-200 text-gray-600 font-medium" onClick={() => setInstitutionTypes([])}>{t('clearAll')}</button>
                      </div>
                      {institutionTypeOptions.map((it) => (
                        <label key={it} className="flex items-center gap-2 py-1.5 cursor-pointer">
                          <Checkbox
                            checked={institutionTypes.includes(it)}
                            onCheckedChange={(checked) => {
                              setInstitutionTypes((prev) => (checked ? [...prev, it] : prev.filter((x) => x !== it)));
                            }}
                          />
                          <span className="text-sm">{it}</span>
                        </label>
                      ))}
                    </PopoverContent>
                  </PopoverStaysOpen>
                )}
                {activeTab === 'live' && (
                  <select
                    className="border rounded px-2 py-1 text-sm"
                    value={deadlineHorizon}
                    onChange={(e) => setDeadlineHorizon(e.target.value)}
                  >
                    <option value="all">{t('allDeadlines')}</option>
                    <option value="0-7">{t('days0_7')}</option>
                    <option value="8-30">{t('days8_30')}</option>
                    <option value="31-90">{t('days31_90')}</option>
                    <option value="90+">{t('days90Plus')}</option>
                    <option value="none">{t('noDeadline')}</option>
                  </select>
                )}
                {activeTab === 'contract' && (
                  <PopoverStaysOpen open={openFilter === 'decisionWindow'} onOpenChange={(o) => setOpenFilter(o ? 'decisionWindow' : null)}>
                    <PopoverTrigger asChild>
                      <Button variant="outline" size="sm" className="h-8 text-sm">
                        {t('decisionWindow')} {decisionWindows.length ? `(${decisionWindows.length})` : ''}
                        <ChevronDown className="w-4 h-4 ml-1" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-56 max-h-60 overflow-y-auto" align="start">
                      <div className="flex flex-wrap gap-2 pb-2 border-b text-xs mb-2">
                        <button type="button" className="px-2 py-1.5 rounded bg-gray-100 hover:bg-gray-200 text-[#00C2C2] font-medium" onClick={() => setDecisionWindows(['0-6', '6-12', '12-24', 'unknown'])}>{t('selectAll')}</button>
                        <button type="button" className="px-2 py-1.5 rounded bg-gray-100 hover:bg-gray-200 text-gray-600 font-medium" onClick={(e) => { e.stopPropagation(); setDecisionWindows([]); }}>{t('clearAll')}</button>
                      </div>
                      {[
                        { value: '0-6', label: t('months0_6') },
                        { value: '6-12', label: t('months6_12') },
                        { value: '12-24', label: t('months12_24') },
                        { value: 'unknown', label: t('unknownExpired') },
                      ].map((opt) => (
                        <label key={opt.value} className="flex items-center gap-2 py-1.5 cursor-pointer block">
                          <Checkbox
                            checked={decisionWindows.includes(opt.value)}
                            onCheckedChange={(checked) => {
                              setDecisionWindows((prev) =>
                                checked ? [...prev, opt.value] : prev.filter((x) => x !== opt.value)
                              );
                            }}
                          />
                          <span className="text-sm">{opt.label}</span>
                        </label>
                      ))}
                    </PopoverContent>
                  </PopoverStaysOpen>
                )}
                {(activeTab === 'contract' || activeTab === 'benchmark' || activeTab === 'displacement') && (
                    <>
                    {activeTab === 'contract' && (
                    <PopoverStaysOpen open={openFilter === 'vendor'} onOpenChange={(o) => setOpenFilter(o ? 'vendor' : null)}>
                      <PopoverTrigger asChild>
                        <Button variant="outline" size="sm" className="h-8 text-sm">
                          {t('vendor')} {contractVendors.length ? `(${contractVendors.length})` : ''}
                          <ChevronDown className="w-4 h-4 ml-1" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-56 max-h-60 overflow-y-auto pt-0" align="start">
                        {/* [2026-10-17] Sticky header. These popovers scroll (Country alone is 130 options, 3,757px of content in a 239px box) and the Select all / Clear all buttons and the search box sat INSIDE the scroll area, so one screen of scrolling put them out of reach - they read as buttons that do nothing. The container's top padding is removed (pt-0) because `sticky top-0` pins to the PADDING-BOX edge: with p-4 the pin sat 16px low and rows scrolled through that strip above the header. */}
                        <div className="sticky top-0 z-10 bg-popover pt-4">
                          <div className="flex flex-wrap gap-2 pb-2 border-b text-xs mb-2">
                            <button type="button" className="px-2 py-1.5 rounded bg-gray-100 hover:bg-gray-200 text-[#00C2C2] font-medium" onClick={() => setContractVendors([...contractVendorOptions])}>{t('selectAll')}</button>
                            <button type="button" className="px-2 py-1.5 rounded bg-gray-100 hover:bg-gray-200 text-gray-600 font-medium" onClick={() => setContractVendors([])}>{t('clearAll')}</button>
                          </div>
                          <Input
                            placeholder={t('typeToSearch')}
                            value={vendorFilterQuery}
                            onChange={(e) => setVendorFilterQuery(e.target.value)}
                            className="h-8 text-sm mb-2"
                            onKeyDown={(e) => e.stopPropagation()}
                          />
                        </div>
                        {contractVendorOptions
                          .filter((c) => !vendorFilterQuery.trim() || c.toLowerCase().includes(vendorFilterQuery.trim().toLowerCase()))
                          .map((c) => (
                          <label key={c} className="flex items-center gap-2 py-1.5 cursor-pointer block">
                            <Checkbox
                              checked={contractVendors.includes(c)}
                              onCheckedChange={(checked) => {
                                setContractVendors((prev) =>
                                  checked ? [...prev, c] : prev.filter((x) => x !== c)
                                );
                              }}
                            />
                            <span className="text-sm">{c}</span>
                          </label>
                        ))}
                      </PopoverContent>
                    </PopoverStaysOpen>
                    )}
                    {activeTab === 'contract' && (
                    <PopoverStaysOpen open={openFilter === 'productName'} onOpenChange={(o) => setOpenFilter(o ? 'productName' : null)}>
                      <PopoverTrigger asChild>
                        <Button variant="outline" size="sm" className="h-8 text-sm">
                          {t('productName')} {contractProductNames.length ? `(${contractProductNames.length})` : ''}
                          <ChevronDown className="w-4 h-4 ml-1" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-56 max-h-60 overflow-y-auto pt-0" align="start">
                        {/* [2026-10-17] Sticky header. These popovers scroll (Country alone is 130 options, 3,757px of content in a 239px box) and the Select all / Clear all buttons and the search box sat INSIDE the scroll area, so one screen of scrolling put them out of reach - they read as buttons that do nothing. The container's top padding is removed (pt-0) because `sticky top-0` pins to the PADDING-BOX edge: with p-4 the pin sat 16px low and rows scrolled through that strip above the header. */}
                        <div className="sticky top-0 z-10 bg-popover pt-4">
                          <div className="flex flex-wrap gap-2 pb-2 border-b text-xs mb-2">
                            <button type="button" className="px-2 py-1.5 rounded bg-gray-100 hover:bg-gray-200 text-[#00C2C2] font-medium" onClick={() => setContractProductNames([...contractProductNameOptions])}>{t('selectAll')}</button>
                            <button type="button" className="px-2 py-1.5 rounded bg-gray-100 hover:bg-gray-200 text-gray-600 font-medium" onClick={() => setContractProductNames([])}>{t('clearAll')}</button>
                          </div>
                          <Input
                            placeholder={t('typeToSearch')}
                            value={productNameFilterQuery}
                            onChange={(e) => setProductNameFilterQuery(e.target.value)}
                            className="h-8 text-sm mb-2"
                            onKeyDown={(e) => e.stopPropagation()}
                          />
                        </div>
                        {contractProductNameOptions
                          .filter((name) => !productNameFilterQuery.trim() || name.toLowerCase().includes(productNameFilterQuery.trim().toLowerCase()))
                          .map((name) => (
                          <label key={name} className="flex items-center gap-2 py-1.5 cursor-pointer block">
                            <Checkbox
                              checked={contractProductNames.includes(name)}
                              onCheckedChange={(checked) => {
                                setContractProductNames((prev) =>
                                  checked ? [...prev, name] : prev.filter((x) => x !== name)
                                );
                              }}
                            />
                            <span className="text-sm">{name}</span>
                          </label>
                        ))}
                      </PopoverContent>
                    </PopoverStaysOpen>
                    )}
                    {activeTab === 'contract' && (
                    <PopoverStaysOpen open={openFilter === 'reseller'} onOpenChange={(o) => setOpenFilter(o ? 'reseller' : null)}>
                      <PopoverTrigger asChild>
                        <Button variant="outline" size="sm" className="h-8 text-sm">
                          {t('reseller')} {contractResellers.length ? `(${contractResellers.length})` : ''}
                          <ChevronDown className="w-4 h-4 ml-1" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-56 max-h-60 overflow-y-auto pt-0" align="start">
                        {/* [2026-10-17] Sticky header. These popovers scroll (Country alone is 130 options, 3,757px of content in a 239px box) and the Select all / Clear all buttons and the search box sat INSIDE the scroll area, so one screen of scrolling put them out of reach - they read as buttons that do nothing. The container's top padding is removed (pt-0) because `sticky top-0` pins to the PADDING-BOX edge: with p-4 the pin sat 16px low and rows scrolled through that strip above the header. */}
                        <div className="sticky top-0 z-10 bg-popover pt-4">
                          <div className="flex flex-wrap gap-2 pb-2 border-b text-xs mb-2">
                            <button type="button" className="px-2 py-1.5 rounded bg-gray-100 hover:bg-gray-200 text-[#00C2C2] font-medium" onClick={() => setContractResellers([...contractResellerOptions])}>{t('selectAll')}</button>
                            <button type="button" className="px-2 py-1.5 rounded bg-gray-100 hover:bg-gray-200 text-gray-600 font-medium" onClick={() => setContractResellers([])}>{t('clearAll')}</button>
                          </div>
                          <Input
                            placeholder={t('typeToSearch')}
                            value={resellerFilterQuery}
                            onChange={(e) => setResellerFilterQuery(e.target.value)}
                            className="h-8 text-sm mb-2"
                            onKeyDown={(e) => e.stopPropagation()}
                          />
                        </div>
                        {contractResellerOptions
                          .filter((name) => !resellerFilterQuery.trim() || name.toLowerCase().includes(resellerFilterQuery.trim().toLowerCase()))
                          .map((name) => (
                          <label key={name} className="flex items-center gap-2 py-1.5 cursor-pointer block">
                            <Checkbox
                              checked={contractResellers.includes(name)}
                              onCheckedChange={(checked) => {
                                setContractResellers((prev) =>
                                  checked ? [...prev, name] : prev.filter((x) => x !== name)
                                );
                              }}
                            />
                            <span className="text-sm">{name}</span>
                          </label>
                        ))}
                      </PopoverContent>
                    </PopoverStaysOpen>
                    )}
                    {activeTab === 'contract' && (
                    <PopoverStaysOpen open={openFilter === 'framework'} onOpenChange={(o) => setOpenFilter(o ? 'framework' : null)}>
                      <PopoverTrigger asChild>
                        <Button variant="outline" size="sm" className="h-8 text-sm">
                          {t('framework')} {contractFrameworks.length ? `(${contractFrameworks.length})` : ''}
                          <ChevronDown className="w-4 h-4 ml-1" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-64 max-h-60 overflow-y-auto pt-0" align="start">
                        {/* [2026-10-17] Sticky header. These popovers scroll (Country alone is 130 options, 3,757px of content in a 239px box) and the Select all / Clear all buttons and the search box sat INSIDE the scroll area, so one screen of scrolling put them out of reach - they read as buttons that do nothing. The container's top padding is removed (pt-0) because `sticky top-0` pins to the PADDING-BOX edge: with p-4 the pin sat 16px low and rows scrolled through that strip above the header. */}
                        <div className="sticky top-0 z-10 bg-popover pt-4">
                          <div className="flex flex-wrap gap-2 pb-2 border-b text-xs mb-2">
                            <button type="button" className="px-2 py-1.5 rounded bg-gray-100 hover:bg-gray-200 text-gray-600 font-medium" onClick={() => setContractFrameworks([])}>{t('clearAll')}</button>
                          </div>
                          <Input
                            placeholder={t('typeToSearch')}
                            value={frameworkFilterQuery}
                            onChange={(e) => setFrameworkFilterQuery(e.target.value)}
                            className="h-8 text-sm mb-2"
                            onKeyDown={(e) => e.stopPropagation()}
                          />
                        </div>
                        {contractFrameworkOptions
                          .filter((name) => !frameworkFilterQuery.trim() || name.toLowerCase().includes(frameworkFilterQuery.trim().toLowerCase()))
                          .map((name) => (
                          <label key={name} className="flex items-center gap-2 py-1.5 cursor-pointer block">
                            <Checkbox
                              checked={contractFrameworks.includes(name)}
                              onCheckedChange={(checked) => {
                                setContractFrameworks((prev) =>
                                  checked ? [...prev, name] : prev.filter((x) => x !== name)
                                );
                              }}
                            />
                            <span className="text-sm">{name}</span>
                          </label>
                        ))}
                      </PopoverContent>
                    </PopoverStaysOpen>
                    )}
                    <PopoverStaysOpen open={openFilter === 'contractInstitutionType'} onOpenChange={(o) => setOpenFilter(o ? 'contractInstitutionType' : null)}>
                      <PopoverTrigger asChild>
                        <Button variant="outline" size="sm" className="h-8 text-sm">
                          {t('institutionType')} {contractInstitutionTypes.length ? `(${contractInstitutionTypes.length})` : ''}
                          <ChevronDown className="w-4 h-4 ml-1" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-56 max-h-60 overflow-y-auto" align="start">
                        <div className="flex flex-wrap gap-2 pb-2 border-b text-xs mb-2">
                          <button type="button" className="px-2 py-1.5 rounded bg-gray-100 hover:bg-gray-200 text-[#00C2C2] font-medium" onClick={() => setContractInstitutionTypes([...contractInstitutionTypeOptions])}>{t('selectAll')}</button>
                          <button type="button" className="px-2 py-1.5 rounded bg-gray-100 hover:bg-gray-200 text-gray-600 font-medium" onClick={() => setContractInstitutionTypes([])}>{t('clearAll')}</button>
                        </div>
                        {contractInstitutionTypeOptions.map((c) => (
                          <label key={c} className="flex items-center gap-2 py-1.5 cursor-pointer block">
                            <Checkbox
                              checked={contractInstitutionTypes.includes(c)}
                              onCheckedChange={(checked) => {
                                setContractInstitutionTypes((prev) =>
                                  checked ? [...prev, c] : prev.filter((x) => x !== c)
                                );
                              }}
                            />
                            <span className="text-sm">{c}</span>
                          </label>
                        ))}
                      </PopoverContent>
                    </PopoverStaysOpen>
                    {activeTab === 'benchmark' && closedSelectedCategories.length > 0 && benchmarkVendorOptions.length > 0 && (
                      <select
                        /* [2026-10-13] This bare <select> computed to 1691px wide and 27px tall
                         * next to sibling filter pills of 106-153px and 32px, so it took a whole
                         * row to itself and pushed the filter bar onto two lines. A base style
                         * gives unclassed selects width:100%, and nothing here overrode it.
                         * w-48 pins the width, h-8 matches the Button size="sm" pills beside it. */
                        className="h-8 w-48 border rounded px-2 text-sm bg-white"
                        value={benchmarkSelectedVendor || ''}
                        onChange={(e) => setBenchmarkSelectedVendor(e.target.value || null)}
                      >
                        <option value="">{t('allVendors')}</option>
                        {benchmarkVendorOptions.map((v) => (
                          <option key={v} value={v}>{v}</option>
                        ))}
                      </select>
                    )}
                    {activeTab === 'contract' && (
                    <PopoverStaysOpen open={openFilter === 'status'} onOpenChange={(o) => setOpenFilter(o ? 'status' : null)}>
                      <PopoverTrigger asChild>
                        <Button variant="outline" size="sm" className="h-8 text-sm">
                          {t('status')} {contractStatuses.length ? `(${contractStatuses.length})` : ''}
                          <ChevronDown className="w-4 h-4 ml-1" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-56 max-h-60 overflow-y-auto" align="start">
                        <div className="flex flex-wrap gap-2 pb-2 border-b text-xs mb-2">
                          <button type="button" className="px-2 py-1.5 rounded bg-gray-100 hover:bg-gray-200 text-[#00C2C2] font-medium" onClick={() => setContractStatuses(['Active', 'Expired', 'Retired'])}>{t('selectAll')}</button>
                          <button type="button" className="px-2 py-1.5 rounded bg-gray-100 hover:bg-gray-200 text-gray-600 font-medium" onClick={() => setContractStatuses([])}>{t('clearAll')}</button>
                        </div>
                        {['Active', 'Expired', 'Retired'].map((st) => (
                          <label key={st} className="flex items-center gap-2 py-1.5 cursor-pointer block">
                            <Checkbox
                              checked={contractStatuses.includes(st)}
                              onCheckedChange={(checked) => {
                                setContractStatuses((prev) => (checked ? [...prev, st] : prev.filter((x) => x !== st)));
                              }}
                            />
                            <span className="text-sm">{st === 'Active' ? t('productFitActive') : st === 'Expired' ? t('expired') : st === 'Retired' ? t('procRetired') : st}</span>
                          </label>
                        ))}
                      </PopoverContent>
                    </PopoverStaysOpen>
                    )}
                    {activeTab === 'contract' && (
                    <PopoverStaysOpen open={openFilter === 'sentiment'} onOpenChange={(o) => setOpenFilter(o ? 'sentiment' : null)}>
                      <PopoverTrigger asChild>
                        <Button variant="outline" size="sm" className="h-8 text-sm">
                          {t('sentiment')} {contractSentiments.length ? `(${contractSentiments.length})` : ''}
                          <ChevronDown className="w-4 h-4 ml-1" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-56 max-h-60 overflow-y-auto" align="start">
                        <div className="flex flex-wrap gap-2 pb-2 border-b text-xs mb-2">
                          <button type="button" className="px-2 py-1.5 rounded bg-gray-100 hover:bg-gray-200 text-[#00C2C2] font-medium" onClick={() => setContractSentiments(['negative', 'neutral', 'positive', 'unknown'])}>{t('selectAll')}</button>
                          <button type="button" className="px-2 py-1.5 rounded bg-gray-100 hover:bg-gray-200 text-gray-600 font-medium" onClick={() => setContractSentiments([])}>{t('clearAll')}</button>
                        </div>
                        {['negative', 'neutral', 'positive', 'unknown'].map((s) => (
                          <label key={s} className="flex items-center gap-2 py-1.5 cursor-pointer block">
                            <Checkbox
                              checked={contractSentiments.includes(s)}
                              onCheckedChange={(checked) => {
                                setContractSentiments((prev) =>
                                  checked ? [...prev, s] : prev.filter((x) => x !== s)
                                );
                              }}
                            />
                            <span className="text-sm">{translateIncumbentSentiment(language, s)}</span>
                          </label>
                        ))}
                      </PopoverContent>
                    </PopoverStaysOpen>
                    )}
                    {activeTab === 'contract' && (
                      <PopoverStaysOpen open={openFilter === 'exit'} onOpenChange={(o) => setOpenFilter(o ? 'exit' : null)}>
                        <PopoverTrigger asChild>
                          <Button type="button" variant="outline" size="sm" className="h-8 text-sm relative z-10">
                            {t('exit')} {contractExitFilter !== 'all' ? `(${contractExitFilter})` : ''}
                            <ChevronDown className="w-4 h-4 ml-1" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-48 p-2" align="start">
                          <p className="text-xs font-medium text-gray-600 mb-2">{t('filterByExitLeverage')}</p>
                          {[
                            { value: 'all', label: t('all') },
                            { value: 'strong', label: t('strong') },
                            { value: 'some', label: t('some') },
                            { value: 'none', label: t('noSignal') },
                          ].map((opt) => (
                            <label key={opt.value} className="flex items-center gap-2 py-1.5 cursor-pointer block">
                              <Checkbox
                                checked={contractExitFilter === opt.value}
                                onCheckedChange={(checked) => checked && setContractExitFilter(opt.value)}
                              />
                              <span className="text-sm">{opt.label}</span>
                            </label>
                          ))}
                        </PopoverContent>
                      </PopoverStaysOpen>
                    )}
                  </>
                )}
              </div>
            </div>

            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <div className="overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0 mb-4">
                <TabsList className="inline-flex w-auto min-w-0 bg-[#ECFFFE]">
                  <TabsTrigger value="live">
                    {t('liveRadar')} ({deferredLiveDealsFiltered.length})
                  </TabsTrigger>
                  {myTendersConfig?.enabled && (
                    <TabsTrigger value="my-tenders">
                      {t('myDeals')} ({myTendersTotalCount})
                    </TabsTrigger>
                  )}
                  <TabsTrigger value="recently">
                    {t('recentlyExpired')} ({deferredRecentlyClosedDealsFiltered.length})
                  </TabsTrigger>
                  <TabsTrigger value="contract">{t('contractIntelligence')}</TabsTrigger>
                  <TabsTrigger value="route-to-buy">{t('routeToBuy')}</TabsTrigger>
                  <TabsTrigger value="displacement">{t('displacementRadar')}</TabsTrigger>
                  <TabsTrigger value="benchmark">{t('benchmarking')}</TabsTrigger>
                </TabsList>
              </div>

              <TabsContent value="live" className="mt-0">
                <div className="flex flex-col gap-4">
                  {mapVisible && (
                    <div className="w-full -mx-2 sm:mx-0">
                      <ProcurementMap
                        deals={deferredLiveDealsFiltered}
                        metric={mapMetric}
                        onMetricChange={setMapMetric}
                        selectedCountries={selectedCountries}
                        mapZoomCountry={mapZoomCountry}
                        onMapCountryClick={(c) => {
                          setMapZoomCountry((prev) => (prev === c ? null : c));
                          setSelectedCountries((prev) => (prev.length === 1 && prev[0] === c ? [] : [c]));
                        }}
                        onClearMapZoom={() => {
                          setMapZoomCountry(null);
                          setSelectedCountries([]);
                        }}
                      />
                    </div>
                  )}
                  <div className="flex-1 min-h-0">
                    {myTendersConfig?.enabled && dealAugmentError && (
                      <div className="mb-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                        {t('procDealActivityUnavailable')}
                      </div>
                    )}
                    <LiveRadarList
                      deals={deferredLiveDealsFiltered}
                      sortConfig={liveSortConfig}
                      setSortConfig={setLiveSortConfig}
                      currentTenant={tenantId}
                      activeProductProfiles={activeProductProfiles}
                      suspendFitLoading={activeProductProfiles.length === 0}
                      onFitOverridesChange={setLiveFitOverrides}
                      dealStatusByDealId={dealStatusColumnActive ? dealAugmentByDealId : undefined}
                      onStatusChange={dealStatusColumnActive ? handleDealStatusChange : undefined}
                      onDealViewLog={myTendersConfig?.enabled ? handleDealViewLog : undefined}
                    />
                  </div>
                </div>
              </TabsContent>

              {myTendersConfig?.enabled && (
                <TabsContent value="my-tenders" className="mt-0">
                  <MyTendersTab
                    ref={myTendersTabRef}
                    tenantId={tenantId}
                    primaryCategories={preferredLiveCategories}
                    liveDeals={deferredLiveDealsFiltered}
                    recentlyExpiredDeals={deferredRecentlyClosedDealsFiltered}
                    dealAugmentByDealId={dealAugmentByDealId}
                    onStatusChange={handleDealStatusChange}
                    onValueOverrideChange={handleDealValueOverrideChange}
                    onDealViewLog={handleDealViewLog}
                    activityUnavailable={dealAugmentError}
                  />
                </TabsContent>
              )}

              <TabsContent value="recently" className="mt-0">
                <div className="flex flex-col gap-4">
                  <RecentlyExpiredSummary deals={deferredRecentlyClosedDealsFiltered} />
                  <div className="flex-1 min-h-0">
                    {myTendersConfig?.enabled && dealAugmentError && (
                      <div className="mb-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                        {t('procDealActivityUnavailable')}
                      </div>
                    )}
                    <LiveRadarList
                      deals={deferredRecentlyClosedDealsFiltered}
                      sortConfig={liveSortConfig}
                      setSortConfig={setLiveSortConfig}
                      currentTenant={tenantId}
                      activeProductProfiles={activeProductProfiles}
                      suspendFitLoading={activeProductProfiles.length === 0}
                      onFitOverridesChange={setLiveFitOverrides}
                      dealStatusByDealId={dealStatusColumnActive ? dealAugmentByDealId : undefined}
                      onStatusChange={dealStatusColumnActive ? handleDealStatusChange : undefined}
                      onDealViewLog={myTendersConfig?.enabled ? handleDealViewLog : undefined}
                    />
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="route-to-buy" className="mt-0">
                {/* Self-contained like Displacement: it fetches its own market data
                    (procurement_route_to_buy, computed live server-side). */}
                {activeTab === 'route-to-buy' ? (
                  <RouteToBuy tenantId={tenantId} categories={closedSelectedCategories} countries={selectedCountries} />
                ) : null}
              </TabsContent>

              <TabsContent value="displacement" className="mt-0">
                {/* A configured tenant brings its own category list, so it does
                    not need a filter selection to render. A scope with no
                    categories (bibliotheca: countries only) still needs one. */}
                {closedSelectedCategories.length === 0 && !resolveDisplacementScope(tenantId)?.categories?.length && !isHardwareTenant(tenantId) ? (
                  <div className="flex flex-col items-center justify-center py-24 gap-4 text-center">
                    <div className="rounded-full bg-[#00C2C2]/10 p-4">
                      <BarChart2 className="w-8 h-8 text-[#00C2C2]" />
                    </div>
                    <p className="text-base font-medium text-foreground">
                      {t('selectCategoryContractTitle')}
                    </p>
                    <p className="text-sm text-muted-foreground max-w-sm">
                      {t('selectCategoryContractDesc')}
                    </p>
                  </div>
                ) : (
                  /* v2: one page per category, read from the global
                     category-intel view. Deliberately NOT fed from
                     insightClosedDealsForCharts — that set is filtered by
                     country/vendor/search, and churn computed from a filtered
                     history is wrong. */
                  isHardwareTenant(tenantId) ? (
                    /* [2026-11-03] Hardware tenants: equipment age × sentiment and replacement
                       outcomes, instead of months-to-expiry and annual churn. With no hardware
                       category selected it reads the tenant's own. */
                    <HardwareReplacementRadar
                      tenantId={tenantId}
                      categories={closedSelectedCategories.some(isHardwareCategory)
                        ? closedSelectedCategories
                        : getDealCategoriesForTenant(tenantId)}
                    />
                  ) : (
                    <CategoryDisplacement
                      categories={closedSelectedCategories}
                      tenantId={tenantId}
                    />
                  )
                )}
              </TabsContent>

              <TabsContent value="benchmark" className="mt-0">
                {closedSelectedCategories.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-24 gap-4 text-center">
                    <div className="rounded-full bg-[#00C2C2]/10 p-4">
                      <BarChart2 className="w-8 h-8 text-[#00C2C2]" />
                    </div>
                    <p className="text-base font-medium text-foreground">
                      {t('selectCategoryBenchmarkTitle')}
                    </p>
                    <p className="text-sm text-muted-foreground max-w-sm">
                      {t('selectCategoryBenchmarkDesc')}
                    </p>
                  </div>
                ) : (
                  <BenchmarkingScatter
                    tenantId={tenantId}
                    points={insightClosedDealsForCharts.length ? insightClosedDealsForCharts : procurementServerTabs.benchmarkPoints}
                    loading={insightClosedDealsForCharts.length ? false : procurementServerTabs.loadingBenchmark}
                    error={insightClosedDealsForCharts.length ? null : procurementServerTabs.benchmarkError}
                    categories={closedSelectedCategories}
                    institutionTypes={contractInstitutionTypes}
                    vendors={benchmarkVendorOptions}
                    selectedVendor={benchmarkSelectedVendor}
                  />
                )}
              </TabsContent>

              <TabsContent value="contract" className="mt-0">
                {categoryLoading && (
                  <div className="h-1 w-full rounded bg-[#00C2C2]/30 overflow-hidden mb-2">
                    <div className="h-full bg-[#00C2C2] animate-pulse w-2/3 rounded" />
                  </div>
                )}
                {closedSelectedCategories.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-24 gap-4 text-center">
                    <div className="rounded-full bg-[#00C2C2]/10 p-4">
                      <BarChart2 className="w-8 h-8 text-[#00C2C2]" />
                    </div>
                    <p className="text-base font-medium text-foreground">
                      {t('selectCategoryContractTitle')}
                    </p>
                    <p className="text-sm text-muted-foreground max-w-sm">
                      {t('selectCategoryContractDesc')}
                    </p>
                  </div>
                ) : isHardwareTenant(contractIntelligenceTenantId) ? (
                  /* [2026-11-03] Hardware tenants (bibliotheca, niryo) land on the replacement-cycle
                     view: kit outlives its contract, so age — not expiry — is the buying signal. The
                     expiry view below is one click away; every other tenant renders it directly. */
                  <ReplacementCycleView
                    tenantId={contractIntelligenceTenantId}
                    categories={closedSelectedCategories}
                    controlledFilters={competitorControlledFilters}
                    search={debouncedSearchTerm}
                    onOpenDetail={openClosedDeal}
                    renderExpiryView={() => (
                      <CompetitorIntelligence
                        key="ci-server"
                        closedDeals={insightClosedDeals}
                        closedDealsFiltered={contractClosedDealsFiltered}
                        onOpenDetail={openClosedDeal}
                        currentTenant={contractIntelligenceTenantId}
                        controlledFilters={competitorControlledFilters}
                        onExitFilterChange={setContractExitFilter}
                        onCountryMapSelect={handleContractMapCountrySelect}
                        useServerAggregates={Boolean(contractIntelligenceTenantId)}
                        procurementFilters={{ search: debouncedSearchTerm }}
                      />
                    )}
                  />
                ) : (
                  <CompetitorIntelligence
                    key="ci-server"
                    closedDeals={insightClosedDeals}
                    closedDealsFiltered={contractClosedDealsFiltered}
                    onOpenDetail={openClosedDeal}
                    currentTenant={contractIntelligenceTenantId}
                    controlledFilters={competitorControlledFilters}
                    onExitFilterChange={setContractExitFilter}
                    onCountryMapSelect={handleContractMapCountrySelect}
                    useServerAggregates={Boolean(contractIntelligenceTenantId)}
                    procurementFilters={{ search: debouncedSearchTerm }}
                  />
                )}
              </TabsContent>
            </Tabs>
          </>
        )}
      </div>
    </div>
  );
}
